/* eslint-disable @typescript-eslint/no-namespace */
import { Decimal } from 'decimal.js';
import { z } from 'zod';

export const decimalSchema = z.string().pipe(z.transform((val) => new Decimal(val)));

// export const decimalSchema = z
//   .preprocess((val) => (typeof val === 'number' ? val.toString() : val), z.string())
//   .refine((val) => {
//     try {
//       new Decimal(val);
//       return true;
//     } catch {
//       return false;
//     }
//   }, 'amount is not a valid decimal') as z.ZodEffects<z.ZodEffects<z.ZodString, string, string>, string, string>;

const checkForHexRegExp = new RegExp('^[0-9a-fA-F]{24}$');

export const PaymentIntentTypeList = [
  'walletTopUp',
  'guestCheckout',
  'orderPayment',
  'subscription',
  'servicePayment',
] as const;
export const DraftStatusList = ['pending', 'approved', 'rejected', 'canceled'] as const;
export const ProductSizeList = ['small', 'medium', 'large', 'overSize'] as const;
export const ShippingCompanyList = ['stpx', 'turboex', 'mayar'] as const;
export const PaymentProviderList = ['wPay', 'moamalat', 'yussorPay', 'masrafyPay'] as const;
export const PaymentIntentStatusList = [
  'awaitingPayment',
  'paid',
  'detected',
  'failed',
  'expired',
  'void',
  'refunded',
] as const;
export const ProductTypeList = ['physical', 'digital'] as const;
export const VendorTypeList = ['voucher', 'general'] as const;
export const WalletTransactionTypeList = ['topUp', 'hold', 'capture', 'release', 'refund', 'payout'] as const;
export const WalletTransactionStatusList = ['pending', 'completed', 'failed'] as const;
export const WPayPaymentIntentStatusList = [
  'new',
  'detected',
  'confirmed',
  'failed',
  'expired',
  'void',
  'refunded',
  'underPaid',
  'overPaid',
] as const;
export const ChatPositionList = ['left', 'right'] as const;
export const TopUpPaymentMethodList = ['moamalat', 'yussorPay', 'masrafyPay', 'crypto'] as const;
export const PaymentMethodList = ['wallet', 'cash'] as const;
export const SectionLayoutList = ['horizontal', 'horizontalIconsOnly', 'vertical', 'grid', 'slides'] as const;
export const ProductsDecisionList = ['approve', 'reject', 'ban'] as const;
export const VendorsDecisionList = ['approve', 'reject', 'ban'] as const;
export const ModerationDecisionList = ['pending', 'approved', 'rejected', 'needsHumanReview'] as const;
export const PublishStatusList = [
  'draft',
  'waitingForReview',
  'waitingForHumanReview',
  'inReview',
  'published',
  'unPublished',
  'rejected',
] as const;
export const SubOrderStatusList = [
  'draft',
  'pending',
  'readyForProcessing',
  'processing',
  'readyForShipping',
  'shipped',
  'delivered',
  'canceled',
  'expired',
] as const;
export const OrderStatusList = [
  'draft',
  'pending',
  'readyForProcessing',
  'processing',
  'readyForShipping',
  'shipped',
  'delivered',
  'canceled',
  'expired',
  'mixed',
] as const;
export const AttributeTypeList = ['color', 'size', 'weight', 'length'] as const;
export const UserTypeList = ['admin', 'vendor', 'customer'] as const;
export const DiffOpList = ['add', 'remove', 'replace', 'move', 'copy', 'test'] as const;

export namespace Enums {
  /**
   * @example
   * 'walletTopUp' => شحن المحفظة
   * 'guestCheckout' => دفع ضيف
   * 'orderPayment' => دفع طلب
   * 'subscription' => اشتراك
   * 'servicePayment' => دفع خدمة
   */
  export type PaymentIntentType = (typeof PaymentIntentTypeList)[number];

  /**
   * @example
   * 'pending' => قيد الانتظار
   * 'approved' => قيد المعالجة
   * 'rejected' => يتعذر
   * 'canceled' => إلغاء
   */
  export type DraftStatus = (typeof DraftStatusList)[number];

  /**
   * @example
   * 'small' => صغير
   * 'medium' => متوسط
   * 'large' => كبير
   * 'overSize' => كبير جداً
   */
  export type ProductSize = (typeof ProductSizeList)[number];

  /**
   * @example
   * 'stpx' => stpx
   * 'turboex' => turboex
   * 'mayar' => mayar
   */
  export type ShippingCompany = (typeof ShippingCompanyList)[number];

  /**
   * @example
   * 'wPay' => دبلبو باي
   * 'moamalat' => معاملات
   * 'yussorPay' => يسر باي
   * 'masrafyPay' => مصرفي باي
   */
  export type PaymentProvider = (typeof PaymentProviderList)[number];

  /**
   * @example
   * 'awaitingPayment' => انتظار الدفع
   * 'paid' => تم الدفع
   * 'detected' => تم الكشف
   * 'failed' => فشلت الشحنة
   * 'expired' => انتهت صلاحية الشحنة
   * 'void' => تم إلغاء
   * 'refunded' => تم استرداد الشحنة
   */
  export type PaymentIntentStatus = (typeof PaymentIntentStatusList)[number];

  /**
   * @example
   * 'physical' => عامة
   * 'digital' => رقمية
   */
  export type ProductType = (typeof ProductTypeList)[number];

  /**
   * @example
   * 'voucher' => كوبون
   * 'general' => عامة
   */
  export type VendorType = (typeof VendorTypeList)[number];

  /**
   * @example
   * 'topUp' => إضافة رصيد
   * 'hold' => حجز مؤقت
   * 'capture' => تحصيل
   * 'release' => إلغاء الحجز
   * 'refund' => ردّ المبلغ
   * 'payout' => دفعة صادرة
   */
  export type WalletTransactionType = (typeof WalletTransactionTypeList)[number];

  /**
   * @example
   * 'pending' => قيد الانتظار
   * 'completed' => مكتمل
   * 'failed' => فاشل
   */
  export type WalletTransactionStatus = (typeof WalletTransactionStatusList)[number];

  /**
   * @example
   * 'new' => جديد
   * 'detected' => تم الكشف
   * 'confirmed' => تم تأكيد الشحنة
   * 'failed' => فشلت الشحنة
   * 'expired' => انتهت صلاحية الشحنة
   * 'void' => تم إلغاء
   * 'refunded' => تم استرداد الشحنة
   * 'underPaid' => دفع أقل
   * 'overPaid' => دفع أكثر
   */
  export type WPayPaymentIntentStatus = (typeof WPayPaymentIntentStatusList)[number];

  /**
   * @example
   * 'left' => يسار
   * 'right' => يمين
   */
  export type ChatPosition = (typeof ChatPositionList)[number];

  /**
   * @example
   * 'moamalat' => بطاقة مصرفية (معاملات)
   * 'yussorPay' => يسر باي (التجاري)
   * 'masrafyPay' => مصرفي باي (الجمهورية)
   * 'crypto' => عملة رقمية (دبليو باي)
   */
  export type TopUpPaymentMethod = (typeof TopUpPaymentMethodList)[number];

  /**
   * @example
   * 'wallet' => رصيد المحفظة
   * 'cash' => نقداً
   */
  export type PaymentMethod = (typeof PaymentMethodList)[number];

  /**
   * @example
   * 'horizontal' => أفقي
   * 'horizontalIconsOnly' => أفقي فقط الرموز
   * 'vertical' => عمودي
   * 'grid' => الشبكة
   * 'slides' => الشرائح
   */
  export type SectionLayout = (typeof SectionLayoutList)[number];

  /**
   * @example
   * 'approve' => موافقة
   * 'reject' => رفض
   * 'ban' => حظر
   */
  export type ProductsDecision = (typeof ProductsDecisionList)[number];

  /**
   * @example
   * 'approve' => موافقة
   * 'reject' => رفض
   * 'ban' => حظر
   */
  export type VendorsDecision = (typeof VendorsDecisionList)[number];

  /**
   * @example
   * 'pending' => قيد الانتظار
   * 'approved' => موافقة
   * 'rejected' => رفض
   * 'needsHumanReview' => يحتاج مراجعة بشرية
   */
  export type ModerationDecision = (typeof ModerationDecisionList)[number];

  /**
   * @example
   * 'draft' => مسودة
   * 'waitingForReview' => في انتظار المراجعة
   * 'waitingForHumanReview' => في انتظار المراجعة
   * 'inReview' => تحت المراجعة
   * 'published' => منشور
   * 'unPublished' => غير منشور
   * 'rejected' => مرفوض
   */
  export type PublishStatus = (typeof PublishStatusList)[number];

  /**
   * @example
   * 'draft' => مسودة
   * 'pending' => قيد الانتظار
   * 'readyForProcessing' => جاهز للمعالجة
   * 'processing' => قيد التجهيز
   * 'readyForShipping' => جاهز للشحن
   * 'shipped' => تم الشحن
   * 'delivered' => تم التوصيل
   * 'canceled' => تم الإلغاء
   * 'expired' => انتهت صلاحية الشحنة
   */
  export type SubOrderStatus = (typeof SubOrderStatusList)[number];

  /**
   * @example
   * 'draft' => مسودة
   * 'pending' => قيد الانتظار
   * 'readyForProcessing' => جاهز للمعالجة
   * 'processing' => قيد التجهيز
   * 'readyForShipping' => جاهز للشحن
   * 'shipped' => تم الشحن
   * 'delivered' => تم التوصيل
   * 'canceled' => تم الإلغاء
   * 'expired' => انتهت صلاحية الشحنة
   * 'mixed' => مختلط
   */
  export type OrderStatus = (typeof OrderStatusList)[number];

  /**
   * @example
   * 'color' => لون
   * 'size' => حجم
   * 'weight' => وزن
   * 'length' => طول
   */
  export type AttributeType = (typeof AttributeTypeList)[number];

  /**
   * @example
   * 'admin' => مدير
   * 'vendor' => متجر
   * 'customer' => عميل
   */
  export type UserType = (typeof UserTypeList)[number];

  /**
   * @example
   * 'add' => add
   * 'remove' => remove
   * 'replace' => replace
   * 'move' => move
   * 'copy' => copy
   * 'test' => test
   */
  export type DiffOp = (typeof DiffOpList)[number];
}

export namespace Dtos {
  export type Attribute = {
    /** المفتاح */
    key: string;
    /** العنوان */
    label: string;
    /** النوع */
    type: Enums.AttributeType;
    /** القيم */
    values: AttributeValue[];
    /** fakeForTesting */
    fakeForTesting: boolean;
  };
  export type AttributeValue = {
    /** المفتاح */
    key: string;
    /** العنوان */
    label: string;
    /** المفاتيح الفرعية */
    subKeys?: string[] | null;
    /** fakeForTesting */
    fakeForTesting: boolean;
  };
  export type UpdateResult = {
    /** تم التحديث */
    didUpdate: boolean;
  };
  export type GenericPagination = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
  };
  export type Pagination = {
    /** الصفحة */
    page: number;
  };
  export type DiffOperation = {
    /** op */
    op: Enums.DiffOp;
    /** path */
    path: string;
    /** value */
    value?: any | null;
    /** from */
    from?: string | null;
  };
  export type DiffOperationCreate = {
    /** op */
    op: Enums.DiffOp;
    /** path */
    path: string;
    /** value */
    value?: any | null;
    /** from */
    from?: string | null;
  };
  export type DiffOperationUpdate = {
    /** op */
    op?: Enums.DiffOp | null;
    /** path */
    path?: string | null;
    /** value */
    value?: any | null;
    /** from */
    from?: string | null;
  };
  export type LanguageString = {
    /** الإنجليزية */
    en: string;
    /** العربية */
    ar: string;
  };
  export type LanguageStringCreate = {
    /** الإنجليزية */
    en: string;
    /** العربية */
    ar: string;
  };
  export type LanguageStringUpdate = {
    /** الإنجليزية */
    en?: string | null;
    /** العربية */
    ar?: string | null;
  };
  export type GeoLocation = {
    /**  */
    id: string;
    /** lat */
    latitude?: number | null;
    /** lon */
    longitude?: number | null;
    /** name */
    name?: string | null;
    /** displayName */
    displayName?: string | null;
    /** type */
    type?: string | null;
    /** addressType */
    addressType?: string | null;
    /** importance */
    importance?: number | null;
    /** placeRank */
    placeRank?: number | null;
    /** noResult */
    noResult: boolean;
    /** query */
    query: string;
    /** shippingCompany */
    shippingCompany: Enums.ShippingCompany;
    /** location */
    location: ShippingExternalLocation;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type GeoLocationCreate = {
    /** lat */
    latitude?: number | null;
    /** lon */
    longitude?: number | null;
    /** name */
    name?: string | null;
    /** displayName */
    displayName?: string | null;
    /** type */
    type?: string | null;
    /** addressType */
    addressType?: string | null;
    /** importance */
    importance?: number | null;
    /** placeRank */
    placeRank?: number | null;
    /** noResult */
    noResult: boolean;
    /** query */
    query: string;
    /** shippingCompany */
    shippingCompany: Enums.ShippingCompany;
    /** location */
    location: ShippingExternalLocation;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type GeoLocationUpdate = {
    /** lat */
    latitude?: number | null;
    /** lon */
    longitude?: number | null;
    /** name */
    name?: string | null;
    /** displayName */
    displayName?: string | null;
    /** type */
    type?: string | null;
    /** addressType */
    addressType?: string | null;
    /** importance */
    importance?: number | null;
    /** placeRank */
    placeRank?: number | null;
    /** noResult */
    noResult?: boolean | null;
    /** query */
    query?: string | null;
    /** shippingCompany */
    shippingCompany?: Enums.ShippingCompany | null;
    /** location */
    location?: ShippingExternalLocation | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ShippingExternalLocation = {
    /**  */
    id: string;
    /** المدينة */
    cityId: string;
    /** المدينة */
    cityName: string;
    /** المنطقة */
    regionId: string;
    /** المنطقة */
    regionName: string;
    /** المنطقة مطلوبة */
    regionRequired?: boolean | null;
    /** مفقود */
    missing: boolean;
    /** شركة الشحن */
    shippingCompany: Enums.ShippingCompany;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ShippingExternalLocationCreate = {
    /** المدينة */
    cityId: string;
    /** المدينة */
    cityName: string;
    /** المنطقة */
    regionId: string;
    /** المنطقة */
    regionName: string;
    /** المنطقة مطلوبة */
    regionRequired?: boolean | null;
    /** مفقود */
    missing: boolean;
    /** شركة الشحن */
    shippingCompany: Enums.ShippingCompany;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ShippingExternalLocationUpdate = {
    /** المدينة */
    cityId?: string | null;
    /** المدينة */
    cityName?: string | null;
    /** المنطقة */
    regionId?: string | null;
    /** المنطقة */
    regionName?: string | null;
    /** المنطقة مطلوبة */
    regionRequired?: boolean | null;
    /** مفقود */
    missing?: boolean | null;
    /** شركة الشحن */
    shippingCompany?: Enums.ShippingCompany | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type Vendor = {
    /**  */
    id: string;
    /** تم الحذف */
    deleted: boolean;
    /** المستخدم */
    userId: string;
    /** المنظمة */
    orgId: string;
    /** الاسم */
    name: string;
    /** العنوان - التصنيف */
    addressLabel?: string | null;
    /** العنوان - الشارع */
    addressStreet?: string | null;
    /** العنوان - الداخلي */
    addressInternal?: string | null;
    /** العنوان - الهاتف */
    addressPhone?: string | null;
    /** العنوان - الهاتف الثاني */
    addressPhone2?: string | null;
    /** العنوان - العرض */
    addressLat?: number | null;
    /** العنوان - الطول */
    addressLng?: number | null;
    /** الشعار */
    logo?: string | null;
    /** blurHash */
    logoBlurHash?: string | null;
    /** الخلفية */
    background?: string | null;
    /** blurHash */
    backgroundBlurHash?: string | null;
    /** الحالة */
    publishStatus: Enums.PublishStatus;
    /** تمت الموافقة */
    approved: boolean;
    /** تم التحقق */
    verified: boolean;
    /** fakeForTesting */
    fakeForTesting: boolean;
    /** طرق الدفع المسموح بها */
    allowedPaymentMethods: Enums.PaymentMethod[];
    /** التسلسل */
    seq: number;
    /** التسلسل */
    subOrderSequence: number;
    /** نوع المتجر */
    vendorType: Enums.VendorType;
    /** مراجعة مزدوجة */
    doubleReview?: boolean | null;
    /** المراجعات */
    moderations?: VendorModeration[] | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type VendorCreate = {
    /** تم الحذف */
    deleted: boolean;
    /** الاسم */
    name: string;
    /** العنوان - التصنيف */
    addressLabel?: string | null;
    /** العنوان - الشارع */
    addressStreet?: string | null;
    /** العنوان - الداخلي */
    addressInternal?: string | null;
    /** العنوان - الهاتف */
    addressPhone?: string | null;
    /** العنوان - الهاتف الثاني */
    addressPhone2?: string | null;
    /** العنوان - العرض */
    addressLat?: number | null;
    /** العنوان - الطول */
    addressLng?: number | null;
    /** الشعار */
    logo?: string | null;
    /** الخلفية */
    background?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type VendorUpdate = {
    /** تم الحذف */
    deleted?: boolean | null;
    /** الاسم */
    name?: string | null;
    /** العنوان - التصنيف */
    addressLabel?: string | null;
    /** العنوان - الشارع */
    addressStreet?: string | null;
    /** العنوان - الداخلي */
    addressInternal?: string | null;
    /** العنوان - الهاتف */
    addressPhone?: string | null;
    /** العنوان - الهاتف الثاني */
    addressPhone2?: string | null;
    /** العنوان - العرض */
    addressLat?: number | null;
    /** العنوان - الطول */
    addressLng?: number | null;
    /** الشعار */
    logo?: string | null;
    /** الخلفية */
    background?: string | null;
    /** طرق الدفع المسموح بها */
    allowedPaymentMethods?: Enums.PaymentMethod[] | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ChatMessage = {
    /** معرف */
    id: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** المستخدم */
    userId: string;
    /** نوع المستخدم */
    userType: Enums.UserType;
    /** الاسم */
    userName?: string | null;
    /** النص */
    text: string;
    /** المرفق */
    attachment?: string | null;
    /** القراءات */
    reads?: ChatMessageRead[] | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ChatMessageCreate = {
    /** النص */
    text: string;
    /** المرفق */
    attachment?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ChatMessageUpdate = {
    /** النص */
    text?: string | null;
    /** المرفق */
    attachment?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ChatMessageRead = {
    /** معرف الرسالة */
    messageId: string;
    /** المستخدم */
    userId: string;
    /** تاريخ القراءة */
    readAt: Date;
  };
  export type ChatMessageReadCreate = {
    /** معرف الرسالة */
    messageId: string;
    /** المستخدم */
    userId: string;
    /** تاريخ القراءة */
    readAt: Date;
  };
  export type ChatMessageReadUpdate = {
    /** معرف الرسالة */
    messageId?: string | null;
    /** المستخدم */
    userId?: string | null;
    /** تاريخ القراءة */
    readAt?: Date | null;
  };
  export type VendorModeration = {
    /**  */
    id: string;
    /** معرف المتجر */
    vendorId: string;
    /** القرار */
    decision: Enums.ModerationDecision;
    /** تمت المراجعة بواسطة */
    reviewedById?: string | null;
    /** تاريخ المراجعة */
    reviewedAt?: Date | null;
    /** الملاحظات */
    notes?: string | null;
    /** فحص الشعار */
    logoCheck?: any | null;
    /** فحص الخلفية */
    backgroundCheck?: any | null;
    /** فحص الألفاظ النابية */
    profanityCheck?: any | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type VendorModerationCreate = {
    /** معرف المتجر */
    vendorId: string;
    /** القرار */
    decision: Enums.ModerationDecision;
    /** تمت المراجعة بواسطة */
    reviewedById?: string | null;
    /** تاريخ المراجعة */
    reviewedAt?: Date | null;
    /** الملاحظات */
    notes?: string | null;
    /** فحص الشعار */
    logoCheck?: any | null;
    /** فحص الخلفية */
    backgroundCheck?: any | null;
    /** فحص الألفاظ النابية */
    profanityCheck?: any | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type VendorModerationUpdate = {
    /** معرف المتجر */
    vendorId?: string | null;
    /** القرار */
    decision?: Enums.ModerationDecision | null;
    /** تمت المراجعة بواسطة */
    reviewedById?: string | null;
    /** تاريخ المراجعة */
    reviewedAt?: Date | null;
    /** الملاحظات */
    notes?: string | null;
    /** فحص الشعار */
    logoCheck?: any | null;
    /** فحص الخلفية */
    backgroundCheck?: any | null;
    /** فحص الألفاظ النابية */
    profanityCheck?: any | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductModeration = {
    /**  */
    id: string;
    /** معرف المنتج */
    productId: string;
    /** القرار */
    decision: Enums.ModerationDecision;
    /** تمت المراجعة بواسطة */
    reviewedById?: string | null;
    /** تاريخ المراجعة */
    reviewedAt?: Date | null;
    /** الملاحظات */
    notes?: string | null;
    /** فحص النص */
    textCheck?: any | null;
    /** فحوصات الصور */
    imageChecks?: any | null;
    /** فحص الألفاظ النابية */
    profanityCheck?: any | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductModerationCreate = {
    /** معرف المنتج */
    productId: string;
    /** القرار */
    decision: Enums.ModerationDecision;
    /** تمت المراجعة بواسطة */
    reviewedById?: string | null;
    /** تاريخ المراجعة */
    reviewedAt?: Date | null;
    /** الملاحظات */
    notes?: string | null;
    /** فحص النص */
    textCheck?: any | null;
    /** فحوصات الصور */
    imageChecks?: any | null;
    /** فحص الألفاظ النابية */
    profanityCheck?: any | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductModerationUpdate = {
    /** معرف المنتج */
    productId?: string | null;
    /** القرار */
    decision?: Enums.ModerationDecision | null;
    /** تمت المراجعة بواسطة */
    reviewedById?: string | null;
    /** تاريخ المراجعة */
    reviewedAt?: Date | null;
    /** الملاحظات */
    notes?: string | null;
    /** فحص النص */
    textCheck?: any | null;
    /** فحوصات الصور */
    imageChecks?: any | null;
    /** فحص الألفاظ النابية */
    profanityCheck?: any | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type SubOrderStatusEvent = {
    /**  */
    id: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** الحالة */
    status: Enums.SubOrderStatus;
    /** السبب بالإنجليزية */
    reasonEn?: string | null;
    /** السبب بالعربية */
    reasonAr?: string | null;
    /** تم التغيير بواسطة */
    changedById?: string | null;
  };
  export type SubOrderStatusEventCreate = {
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** الحالة */
    status: Enums.SubOrderStatus;
    /** السبب بالإنجليزية */
    reasonEn?: string | null;
    /** السبب بالعربية */
    reasonAr?: string | null;
    /** تم التغيير بواسطة */
    changedById?: string | null;
  };
  export type SubOrderStatusEventUpdate = {
    /** معرف الطلب الفرعي */
    subOrderId?: string | null;
    /** الحالة */
    status?: Enums.SubOrderStatus | null;
    /** السبب بالإنجليزية */
    reasonEn?: string | null;
    /** السبب بالعربية */
    reasonAr?: string | null;
    /** تم التغيير بواسطة */
    changedById?: string | null;
  };
  export type Order = {
    /** رقم الطلب */
    orderNo: string;
    /** معرف الشحنة */
    idempotencyKey: string;
    /**  */
    id: string;
    /** تاريخ الطلب */
    orderDate: Date;
    /** المستخدم */
    userId: string;
    /** الاجمالي */
    total: number;
    /** الاجمالي */
    subTotal: number;
    /** fakeForTesting */
    fakeForTesting: boolean;
    /** معرف عنوان الشحن */
    shippingAddressId?: string | null;
    /** العنوان - التصنيف */
    addressLabel?: string | null;
    /** العنوان - الشارع */
    addressStreet: string;
    /** العنوان - الداخلي */
    addressInternal?: string | null;
    /** العنوان - الهاتف */
    addressPhone: string;
    /** العنوان - الهاتف الثاني */
    addressPhone2?: string | null;
    /** العنوان - العرض */
    addressLat: number;
    /** العنوان - الطول */
    addressLng: number;
    /** الطلبات الفرعية */
    subOrders: SubOrder[];
    /** الحالة */
    status: Enums.OrderStatus;
    /** طريقة الدفع */
    paymentMethod: Enums.PaymentMethod;
    /** خارجية */
    external?: boolean | null;
    /** الملاحظات */
    notes?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type OrderCreate = {
    /** معرف الشحنة */
    idempotencyKey: string;
    /** العنوان - التصنيف */
    addressLabel?: string | null;
    /** العنوان - الشارع */
    addressStreet: string;
    /** العنوان - الداخلي */
    addressInternal?: string | null;
    /** العنوان - الهاتف */
    addressPhone: string;
    /** العنوان - الهاتف الثاني */
    addressPhone2?: string | null;
    /** العنوان - العرض */
    addressLat: number;
    /** العنوان - الطول */
    addressLng: number;
    /** طريقة الدفع */
    paymentMethod: Enums.PaymentMethod;
    /** الملاحظات */
    notes?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type SubOrder = {
    /**  */
    id: string;
    /** معرف الحفظ */
    walletHoldId?: string | null;
    /** المستخدم */
    userId: string;
    /** المنظمة */
    orgId: string;
    /** المنتجات */
    orderProducts: OrderProduct[];
    /** الاجمالي */
    total: number;
    /** الاجمالي */
    subTotal: number;
    /** الحالة */
    status: Enums.SubOrderStatus;
    /** الدردشة */
    chatMessages?: ChatMessage[] | null;
    /** أحداث الحالة */
    statusEvents?: SubOrderStatusEvent[] | null;
    /** الملاحظات الإنجليزية */
    notesEn?: string | null;
    /** الملاحظات العربية */
    notesAr?: string | null;
    /** معرف الشحنة */
    idempotencyKey: string;
  };
  export type SubOrderCreate = {
    /** المنتجات */
    orderProducts: OrderProduct[];
    /** الملاحظات الإنجليزية */
    notesEn?: string | null;
    /** الملاحظات العربية */
    notesAr?: string | null;
    /** معرف الشحنة */
    idempotencyKey: string;
  };
  export type SubOrderUpdate = {
    /** المنتجات */
    orderProducts?: OrderProduct[] | null;
    /** الملاحظات الإنجليزية */
    notesEn?: string | null;
    /** الملاحظات العربية */
    notesAr?: string | null;
    /** معرف الشحنة */
    idempotencyKey?: string | null;
  };
  export type PaymentIntentMetaData = {
    /** معرف المعاملة */
    transactionId?: string | null;
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف العميل */
    customerId?: string | null;
    /** بريد العميل */
    customerEmail?: string | null;
  };
  export type PaymentIntentMetaDataCreate = {
    /** معرف المعاملة */
    transactionId?: string | null;
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف العميل */
    customerId?: string | null;
    /** بريد العميل */
    customerEmail?: string | null;
  };
  export type PaymentIntentMetaDataUpdate = {
    /** معرف المعاملة */
    transactionId?: string | null;
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف العميل */
    customerId?: string | null;
    /** بريد العميل */
    customerEmail?: string | null;
  };
  export type PaymentIntentWebhook = {
    /**  */
    id: string;
    /** الحالة */
    status: Enums.WPayPaymentIntentStatus;
    /** بيانات اضافية */
    metadata: PaymentIntentMetaData;
    /** المبلغ */
    amount: number;
    /** رمل */
    sandbox: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductVariationMetadata = {
    /** MNO */
    voucherMno?: string | null;
    /** كود المنتج MNO */
    voucherProductCode?: string | null;
    /** المبلغ */
    voucherAmount?: number | null;
  };
  export type ProductVariationMetadataCreate = {
    /** MNO */
    voucherMno?: string | null;
    /** كود المنتج MNO */
    voucherProductCode?: string | null;
    /** المبلغ */
    voucherAmount?: number | null;
  };
  export type ProductVariationMetadataUpdate = {
    /** MNO */
    voucherMno?: string | null;
    /** كود المنتج MNO */
    voucherProductCode?: string | null;
    /** المبلغ */
    voucherAmount?: number | null;
  };
  export type ProductVariation = {
    /**  */
    id: string;
    /** الصورة */
    image?: string | null;
    /** السعر */
    price: number;
    /** الخصم */
    discount?: number | null;
    /** الكمية */
    stock: number;
    /** الخصائص */
    attributes: VariationAttribute[];
    /** البيانات الوصفية */
    metadata?: ProductVariationMetadata | null;
  };
  export type ProductVariationCreate = {
    /** الصورة */
    image?: string | null;
    /** السعر */
    price: number;
    /** الخصم */
    discount?: number | null;
    /** الكمية */
    stock: number;
    /** الخصائص */
    attributes: VariationAttribute[];
    /** البيانات الوصفية */
    metadata?: ProductVariationMetadata | null;
  };
  export type ProductVariationUpdate = {
    /** الصورة */
    image?: string | null;
    /** السعر */
    price?: number | null;
    /** الخصم */
    discount?: number | null;
    /** الكمية */
    stock?: number | null;
    /** الخصائص */
    attributes?: VariationAttribute[] | null;
    /** البيانات الوصفية */
    metadata?: ProductVariationMetadata | null;
  };
  export type OrderProduct = {
    /**  */
    id: string;
    /** معرف المنتج */
    productId: string;
    /** معرف الخصائص */
    variationId?: string | null;
    /** اسم المنتج */
    productName: string;
    /** صورة المنتج */
    productImage?: string | null;
    /** نوع المنتج */
    productType: Enums.ProductType;
    /** حجم المنتج */
    productSize: Enums.ProductSize;
    /** معرف التصنيف */
    categoryId: string;
    /** اسم التصنيف */
    categoryName: string;
    /** معرف المتجر */
    vendorId: string;
    /** اسم المتجر */
    vendorName: string;
    /** شعار المتجر */
    vendorLogo?: string | null;
    /** صورة الخصائص */
    variationImage?: string | null;
    /** خصائص المتغير */
    variationAttributes?: any | null;
    /** الكمية */
    quantity: number;
    /** السعر النهائي */
    finalPrice: number;
    /** بيانات المنتج */
    metadata?: OrderProductMetaData | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type OrderProductCreate = {
    /** معرف المنتج */
    productId: string;
    /** معرف الخصائص */
    variationId?: string | null;
    /** اسم المنتج */
    productName: string;
    /** صورة المنتج */
    productImage?: string | null;
    /** نوع المنتج */
    productType: Enums.ProductType;
    /** حجم المنتج */
    productSize: Enums.ProductSize;
    /** معرف التصنيف */
    categoryId: string;
    /** اسم التصنيف */
    categoryName: string;
    /** معرف المتجر */
    vendorId: string;
    /** اسم المتجر */
    vendorName: string;
    /** شعار المتجر */
    vendorLogo?: string | null;
    /** صورة الخصائص */
    variationImage?: string | null;
    /** خصائص المتغير */
    variationAttributes?: any | null;
    /** الكمية */
    quantity: number;
    /** السعر النهائي */
    finalPrice: number;
    /** بيانات المنتج */
    metadata?: OrderProductMetaData | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type OrderProductMetaData = {
    /** الكوبون */
    voucher?: Voucher | null;
  };
  export type OrderProductMetaDataCreate = {
    /** الكوبون */
    voucher?: Voucher | null;
  };
  export type OrderProductMetaDataUpdate = {
    /** الكوبون */
    voucher?: Voucher | null;
  };
  export type Category = {
    /**  */
    id: string;
    /** الاسم */
    name: string;
    /** fakeForTesting */
    fakeForTesting: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type CategoryCreate = {
    /** الاسم */
    name: string;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type CategoryUpdate = {
    /** الاسم */
    name?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type NumberRange = {
    /** الحد الأدنى */
    min: number;
    /** الحد الأقصى */
    max: number;
  };
  export type NumberRangeCreate = {
    /** الحد الأدنى */
    min: number;
    /** الحد الأقصى */
    max: number;
  };
  export type NumberRangeUpdate = {
    /** الحد الأدنى */
    min?: number | null;
    /** الحد الأقصى */
    max?: number | null;
  };
  export type Voucher = {
    /**  */
    id: string;
    /** تم البيع */
    locked: boolean;
    /** كود MNO */
    mnoCode?: string | null;
    /** المبلغ */
    amount?: number | null;
    /** الرقم */
    sN?: string | null;
    /** الكود */
    code?: string | null;
    /** تاريخ الانتهاء */
    expiryDate?: Date | null;
    /** المنتج */
    product?: string | null;
    /** كود المنتج */
    productCode?: string | null;
    /** نوع MNO */
    mnoType?: string | null;
    /** السعر */
    price?: number | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type Product = {
    /**  */
    id: string;
    /** تم الحذف */
    deleted: boolean;
    /** الحجم */
    size: Enums.ProductSize;
    /** المستخدم */
    userId: string;
    /** المنظمة */
    orgId: string;
    /** الاسم */
    name: string;
    /** الوصف */
    description?: string | null;
    /** الصور */
    images?: string[] | null;
    /** السعر */
    price?: number | null;
    /** السعر */
    discount?: number | null;
    /** الكمية */
    stock?: number | null;
    /** عدد التقييمات */
    ratingCount: number;
    /** متوسط التقييمات */
    ratingAvg?: number | null;
    /** الحالة */
    publishStatus: Enums.PublishStatus;
    /** ملاحظات النشر */
    publishNotes?: string | null;
    /** المتجر */
    vendor: Vendor;
    /** المتجر */
    vendorId: string;
    /** التصنيف */
    category: Category;
    /** الخصائص */
    variations?: ProductVariation[] | null;
    /** fakeForTesting */
    fakeForTesting: boolean;
    /** الخلفية */
    bgColor?: string | null;
    /** النوع */
    type: Enums.ProductType;
    /** blurHash */
    blurHash?: string | null;
    /** مراجعة مزدوجة */
    doubleReview?: boolean | null;
    /** المراجعات */
    moderations?: ProductModeration[] | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductCreate = {
    /** الحجم */
    size: Enums.ProductSize;
    /** الاسم */
    name: string;
    /** الوصف */
    description?: string | null;
    /** الصور */
    images?: string[] | null;
    /** السعر */
    price?: number | null;
    /** السعر */
    discount?: number | null;
    /** الكمية */
    stock?: number | null;
    /** الخصائص */
    variations?: ProductVariation[] | null;
    /** الخلفية */
    bgColor?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductUpdate = {
    /** الحجم */
    size?: Enums.ProductSize | null;
    /** الاسم */
    name?: string | null;
    /** الوصف */
    description?: string | null;
    /** الصور */
    images?: string[] | null;
    /** السعر */
    price?: number | null;
    /** السعر */
    discount?: number | null;
    /** الكمية */
    stock?: number | null;
    /** الخصائص */
    variations?: ProductVariation[] | null;
    /** الخلفية */
    bgColor?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type VariationAttribute = {
    /**  */
    id: string;
    /** النوع العنوان */
    typeLabel: string;
    /** النوع المفتاح */
    typeKey: string;
    /** النوع */
    type: Enums.AttributeType;
    /** العنوان */
    label: string;
    /** المفتاح */
    key: string;
  };
  export type VariationAttributeCreate = {
    /** النوع العنوان */
    typeLabel: string;
    /** النوع المفتاح */
    typeKey: string;
    /** النوع */
    type: Enums.AttributeType;
    /** العنوان */
    label: string;
    /** المفتاح */
    key: string;
  };
  export type VariationAttributeUpdate = {
    /** النوع العنوان */
    typeLabel?: string | null;
    /** النوع المفتاح */
    typeKey?: string | null;
    /** النوع */
    type?: Enums.AttributeType | null;
    /** العنوان */
    label?: string | null;
    /** المفتاح */
    key?: string | null;
  };
  export type Attachment = {
    /**  */
    id: string;
    /** الوصف */
    description?: string | null;
    /** الرابط */
    url: string;
    /** المفتاح */
    key: string;
    /** المستخدم */
    userId: string;
    /** المنظمة */
    orgId: string;
    /** fakeForTesting */
    fakeForTesting: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type AttachmentCreate = {
    /** الوصف */
    description?: string | null;
    /** الرابط */
    url: string;
    /** المفتاح */
    key: string;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type AttachmentUpdate = {
    /** الوصف */
    description?: string | null;
    /** الرابط */
    url?: string | null;
    /** المفتاح */
    key?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductReview = {
    /**  */
    id: string;
    /** المستخدم */
    userId: string;
    /** الاسم */
    userName: string;
    /** المنظمة */
    orgId: string;
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** المنتج */
    productId: string;
    /** التقييم */
    rating: number;
    /** المراجعة */
    review?: string | null;
    /** fakeForTesting */
    fakeForTesting: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductReviewCreate = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** المنتج */
    productId: string;
    /** التقييم */
    rating: number;
    /** المراجعة */
    review?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type ProductReviewUpdate = {
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف الطلب الفرعي */
    subOrderId?: string | null;
    /** المنتج */
    productId?: string | null;
    /** التقييم */
    rating?: number | null;
    /** المراجعة */
    review?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type Address = {
    /**  */
    id: string;
    /** التصنيف */
    label: string;
    /** الشارع */
    street: string;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone: string;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude: number;
    /** الطول */
    longitude: number;
  };
  export type AddressCreate = {
    /** التصنيف */
    label: string;
    /** الشارع */
    street: string;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone: string;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude: number;
    /** الطول */
    longitude: number;
  };
  export type AddressUpdate = {
    /** التصنيف */
    label?: string | null;
    /** الشارع */
    street?: string | null;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone?: string | null;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude?: number | null;
    /** الطول */
    longitude?: number | null;
  };
  export type UserAddress = {
    /**  */
    id: string;
    /** المستخدم */
    userId: string;
    /** التصنيف */
    label: string;
    /** الشارع */
    street: string;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone: string;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude: number;
    /** الطول */
    longitude: number;
    /** العنوان الافتراضي */
    isDefault: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type UserAddressCreate = {
    /** التصنيف */
    label: string;
    /** الشارع */
    street: string;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone: string;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude: number;
    /** الطول */
    longitude: number;
    /** العنوان الافتراضي */
    isDefault: boolean;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type UserAddressUpdate = {
    /** التصنيف */
    label?: string | null;
    /** الشارع */
    street?: string | null;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone?: string | null;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude?: number | null;
    /** الطول */
    longitude?: number | null;
    /** العنوان الافتراضي */
    isDefault?: boolean | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type User = {
    /**  */
    id: string;
    /**  */
    authId: string;
    /**  */
    name?: string | null;
    /**  */
    email?: string | null;
    /**  */
    phone?: string | null;
    /**  */
    avatar?: string | null;
    /** مدير */
    isAdmin: boolean;
    /** المنظمة */
    orgId: string;
    /**  */
    birthdate?: Date | null;
    /** العناوين */
    addresses?: UserAddress[] | null;
    /** معرفات المتاجر */
    vendorIds?: string[] | null;
    /** رموز FCM */
    customerFcmTokens?: string[] | null;
    /** رموز FCM */
    adminFcmTokens?: string[] | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type UserUpdate = {
    /**  */
    authId?: string | null;
    /**  */
    name?: string | null;
    /**  */
    phone?: string | null;
    /**  */
    avatar?: string | null;
    /** مدير */
    isAdmin?: boolean | null;
    /**  */
    birthdate?: Date | null;
    /** معرفات المتاجر */
    vendorIds?: string[] | null;
    /** رموز FCM */
    customerFcmTokens?: string[] | null;
    /** رموز FCM */
    adminFcmTokens?: string[] | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type WalletTransaction = {
    /**  */
    id: string;
    /** معرف المحفظة */
    walletId: string;
    /** المبلغ */
    amount: Decimal;
    /** العملة */
    currency: string;
    /** النوع */
    type: Enums.WalletTransactionType;
    /** الحالة */
    status: Enums.WalletTransactionStatus;
    /** معرف التكرار */
    idempotencyKey: string;
    /** المزود */
    provider?: string | null;
    /** نية المزود */
    providerIntent?: string | null;
    /** تاريخ التأكيد */
    confirmedAt?: Date | null;
    /** معرف المنظمة */
    orgId?: string | null;
    /** دفع المزود */
    providerPayout?: string | null;
    /** تاريخ المعالجة */
    processedAt?: Date | null;
    /** المبلغ بالدولار الأمريكي */
    usdAmount?: Decimal | null;
    /** نسبة التبادل */
    exchangeRate?: Decimal | null;
    /** طريقة الشحن */
    topUpPaymentMethod?: Enums.TopUpPaymentMethod | null;
    /** معرف العملية */
    orderId?: string | null;
    /** معرف العملية الفرعي */
    subOrderId?: string | null;
    /** المستخدم */
    userId: string;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type WalletTransactionUpdate = {
    /** معرف المحفظة */
    walletId?: string | null;
    /** المبلغ */
    amount?: Decimal | null;
    /** العملة */
    currency?: string | null;
    /** النوع */
    type?: Enums.WalletTransactionType | null;
    /** الحالة */
    status?: Enums.WalletTransactionStatus | null;
    /** معرف التكرار */
    idempotencyKey?: string | null;
    /** المزود */
    provider?: string | null;
    /** نية المزود */
    providerIntent?: string | null;
    /** تاريخ التأكيد */
    confirmedAt?: Date | null;
    /** معرف المنظمة */
    orgId?: string | null;
    /** دفع المزود */
    providerPayout?: string | null;
    /** تاريخ المعالجة */
    processedAt?: Date | null;
    /** طريقة الشحن */
    topUpPaymentMethod?: Enums.TopUpPaymentMethod | null;
    /** معرف العملية */
    orderId?: string | null;
    /** معرف العملية الفرعي */
    subOrderId?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type Wallet = {
    /**  */
    id: string;
    /** المستخدم */
    userId: string;
    /** الرصيد */
    balance: Decimal;
    /** الرصيد المحجوز */
    holdBalance: Decimal;
    /** fakeForTesting */
    fakeForTesting: boolean;
  };
  export type WalletUpdate = {
    /** الرصيد */
    balance?: Decimal | null;
    /** الرصيد المحجوز */
    holdBalance?: Decimal | null;
  };
  export type PaymentIntent = {
    /**  */
    id: string;
    /** نوع الشحن */
    type: Enums.PaymentIntentType;
    /** المستخدم */
    userId?: string | null;
    /** المبلغ */
    amount: Decimal;
    /** المبلغ بالدولار الأمريكي */
    usdAmount?: Decimal | null;
    /** نسبة التبادل */
    exchangeRate?: Decimal | null;
    /** طريقة الشحن */
    topUpPaymentMethod: Enums.TopUpPaymentMethod;
    /** رابط الدفع */
    checkoutUrl?: string | null;
    /** الحالة */
    status: Enums.PaymentIntentStatus;
    /** معرف الطلب */
    orderId?: string | null;
    /** البطاقة الشخصية */
    identityCard?: string | null;
    /** جلسة Masarat */
    masaratSessionToken?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type PaymentIntentUpdate = {
    /** المبلغ */
    amount?: Decimal | null;
    /** طريقة الشحن */
    topUpPaymentMethod?: Enums.TopUpPaymentMethod | null;
    /** البطاقة الشخصية */
    identityCard?: string | null;
    /** جلسة Masarat */
    masaratSessionToken?: string | null;
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type SharedCartItem = {
    /** المنتج */
    productId: string;
    /** المتغير */
    variantId?: string | null;
    /** الكمية */
    quantity: number;
  };
  export type SharedCart = {
    /**  */
    id: string;
    /** المستخدم */
    userId: string;
    /** المنتجات */
    items: SharedCartItem[];
    createdAt?: Date;
    updatedAt?: Date;
  };
  export type UserWishlist = {
    /**  */
    id: string;
    /** المستخدم */
    userId: string;
    /** المنتج */
    productId: string;
  };
  export type WPayWebhook = {
    /**  */
    id: string;
    /** الحمولة */
    payload?: any | null;
  };
  export type ShippingFee = {
    /**  */
    id: string;
    /** البيانات */
    data?: any | null;
  };
  export type SharedCartBody = {
    /** المنتجات */
    items: SharedCartItem[];
  };
  export type ShareCartResponse = {
    /** معرف التبادل */
    id: string;
  };
  export type GetSharedCartPathParams = {
    /** معرف التبادل */
    id: string;
  };
  export type GetSharedCartItem = {
    /** المنتج */
    product: Product;
    /** المتغير */
    variantId?: string | null;
    /** الكمية */
    quantity: number;
  };
  export type GetSharedCartResponse = {
    /** المنتجات */
    items: GetSharedCartItem[];
  };
  export type OrderProductForShippingResponse = {
    /** التكلفة */
    cost: number;
    /** مدة التسليم */
    deliveryTime: LanguageString;
  };
  export type OrderProductForShippingCost = {
    /** حجم المنتج */
    productSize: Enums.ProductSize;
    /** عنوان المتجر */
    vendorAddress: Address;
    /** الكمية */
    quantity: number;
  };
  export type CalculateShippingCostBody = {
    /** المنتجات */
    orderProducts: OrderProductForShippingCost[];
    /** العنوان */
    customerAddress: Address;
  };
  export type VendorGetProductByIdPathParams = {
    /** معرف المنتج */
    id: string;
  };
  export type GetProductByIdPathParams = {
    /** معرف المنتج */
    id: string;
  };
  export type GetSubOrderChatResponse = {
    /** الرسائل */
    chat: ChatMessage[];
  };
  export type GetSubOrderChatPathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type GetOrderByIdPathParams = {
    /** معرف الطلب */
    id: string;
  };
  export type ProductsListResponse = {
    /** المنتجات */
    products: Product[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type ProductsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** معرف المتجر */
    vendorId?: string | null;
    /** search */
    search?: string | null;
    /** حالة النشر */
    publishStatus?: Enums.PublishStatus | null;
    /** السعر من */
    priceFrom?: number | null;
    /** السعر إلى */
    priceTo?: number | null;
    /** التصنيف */
    category?: string | null;
    /** التقييم */
    stars?: number | null;
  };
  export type RelatedProductsListPathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type RelatedProductsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
  };
  export type RelatedProductsListResponse = {
    /** المنتجات ذات الصلة */
    relatedProducts: Product[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type ProductReviewsListPathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type ProductReviewsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
  };
  export type ProductReviewsListResponse = {
    /** مراجعات المنتج */
    productReviews: ProductReview[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type RequestToPublishProductPathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type UnPublishProductPathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type RequestToPublishVendorPathParams = {
    /** معرف المتجر */
    vendorId: string;
  };
  export type UnPublishVendorPathParams = {
    /** معرف المنتج */
    vendorId: string;
  };
  export type GetVendorByIdQueryParams = {
    /** حالة المتجر */
    withDraft?: boolean | null;
  };
  export type GetVendorByIdPathParams = {
    /** معرف المتجر */
    vendorId: string;
  };
  export type VendorsListResponse = {
    /** المتاجر */
    vendors: Vendor[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type VendorsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** حالة المتجر */
    publishStatus?: Enums.PublishStatus | null;
    /** search */
    search?: string | null;
  };
  export type VendorsListForVendorsResponse = {
    /** المتاجر */
    vendors: Vendor[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type VendorsListForVendorsQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** حالة المتجر */
    publishStatus?: Enums.PublishStatus | null;
    /** search */
    search?: string | null;
  };
  export type CategoriesListResponse = {
    /** التصنيفات */
    categories: Category[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type CategoriesListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** معرف الأصل */
    parentId?: string | null;
    /** search */
    search?: string | null;
  };
  export type OrdersListResponse = {
    /** الطلبات */
    orders: Order[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type OrdersListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** order status */
    status?: Enums.SubOrderStatus | null;
    /** search */
    search?: string | null;
  };
  export type OrdersListCustomersResponse = {
    /** الطلبات */
    orders: Order[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type OrdersListOrdersListCustomersQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** order status */
    status?: Enums.SubOrderStatus | null;
    /** search */
    search?: string | null;
  };
  export type AdminProductsListResponse = {
    /** المنتجات */
    products: Product[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type AdminProductsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** معرف المتجر */
    vendorId?: string | null;
    /** حالة النشر */
    publishStatus?: Enums.PublishStatus | null;
    /** search */
    search?: string | null;
  };
  export type AdminProductsDecisionItem = {
    /** معرف المنتج */
    productId: string;
    /** القرار */
    decision: Enums.ProductsDecision;
    /** السبب */
    publishNotes?: string | null;
  };
  export type AdminProductsDecisionList = {
    /** العناصر */
    decisionList: AdminProductsDecisionItem[];
  };
  export type AdminVendorsListResponse = {
    /** المتاجر */
    vendors: Vendor[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type AdminVendorsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** حالة النشر */
    publishStatus?: Enums.PublishStatus | null;
    /** search */
    search?: string | null;
  };
  export type AdminVendorsDecisionItem = {
    /** معرف المنتج */
    vendorId: string;
    /** القرار */
    decision: Enums.VendorsDecision;
    /** السبب */
    publishNotes?: string | null;
  };
  export type AdminVendorsDecisionList = {
    /** العناصر */
    decisionList: AdminVendorsDecisionItem[];
  };
  export type MarkOrderAsReadyForShippingPathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type MarkOrderAsProcessingPathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type CancelOrderPathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type CancelOrderBody = {
    /** السبب */
    reason?: string | null;
  };
  export type AddToWishlistPathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type RemoveFromWishlistPathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type WishListResponse = {
    /** المنتجات */
    products: Product[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type WishListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** search */
    search?: string | null;
  };
  export type ProductsListByFilterResponse = {
    /** المنتجات */
    products: Product[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type ProductsListByFilterQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** filter */
    filter?: string | null;
    /** search */
    search?: string | null;
  };
  export type VendorsListByFilterResponse = {
    /** المتاجر */
    vendors: Vendor[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type VendorsListByFilterQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** filter */
    filter?: string | null;
  };
  export type Slides = {
    /** الصورة */
    image: string;
    /** blurHash */
    blurHash: string;
    /** الرابط */
    link?: string | null;
  };
  export type Section = {
    /** العنوان */
    title?: string | null;
    /** عرض الكل */
    viewAllFilter?: string | null;
    /** التصميم */
    layout: Enums.SectionLayout;
    /** العنوان الفرعي */
    subtitle?: string | null;
    /** الرابط */
    link?: string | null;
    /** المنتجات */
    products?: Product[] | null;
    /** المتاجر */
    vendors?: Vendor[] | null;
    /** الشرائح */
    slides?: Slides[] | null;
  };
  export type LandingPageResponse = {
    /** الأقسام */
    sections: Section[];
  };
  export type AddAddressBody = {
    /** التصنيف */
    label: string;
    /** الشارع */
    street: string;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone: string;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude: number;
    /** الطول */
    longitude: number;
  };
  export type SendChatMessagePathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type SendChatMessageBody = {
    /** النص */
    text: string;
    /** المرفق */
    attachment?: string | null;
  };
  export type MarkChatMessageAsSeenPathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** معرف الرسالة */
    messageId: string;
  };
  export type UpdateChatMessagePathParams = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** معرف الرسالة */
    messageId: string;
  };
  export type UpdateChatMessageBody = {
    /** النص */
    text?: string | null;
    /** المرفق */
    attachment?: string | null;
  };
  export type UpdateAddressPathParams = {
    /** معرف العنوان */
    addressId: string;
  };
  export type UpdateAddressBody = {
    /** التصنيف */
    label?: string | null;
    /** الشارع */
    street?: string | null;
    /** العنوان الداخلي */
    internalAddress?: string | null;
    /** الهاتف */
    phone?: string | null;
    /** رقم التليفون الثاني */
    phone2?: string | null;
    /** العرض */
    latitude?: number | null;
    /** الطول */
    longitude?: number | null;
  };
  export type DeleteAddressPathParams = {
    /** معرف العنوان */
    addressId: string;
  };
  export type GetPublicOrderPathParams = {
    /** معرف الطلب */
    orderId: string;
  };
  export type PlaceOrderProduct = {
    /** المنتج */
    productId: string;
    /** الخصائص */
    variationId?: string | null;
    /** الكمية */
    quantity: number;
    /** السعر النهائي */
    finalPrice: number;
    /** معرف الشحنة */
    idempotencyKey: string;
  };
  export type PlaceOrderBody = {
    /** المنتجات */
    orderProducts: PlaceOrderProduct[];
    /** العنوان */
    address?: Address | null;
    /** طريقة الدفع */
    paymentMethod: Enums.PaymentMethod;
    /** معرف الشحنة */
    idempotencyKey: string;
    /** الملاحظات */
    notes?: string | null;
  };
  export type BuyNowBody = {
    /** المنتج */
    productId: string;
    /** الخصائص */
    variationId: string;
    /** السعر النهائي */
    finalPrice: number;
    /** طريقة الدفع */
    paymentMethod: Enums.PaymentMethod;
    /** معرف الشحنة */
    idempotencyKey: string;
  };
  export type GetUserWalletQueryParams = {
    /** معرف المستخدم */
    userId?: string | null;
  };
  export type WalletReleasePathParams = {
    /** معرف المستخدم */
    userId: string;
    /** معرف الحفظ */
    holdId: string;
  };
  export type WalletReleaseResponse = {
    /** النجاح */
    success: boolean;
    /** هل تم الحفظ بالفعل */
    alreadyReleased: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletMultiReleasePathParams = {
    /** معرف المستخدم */
    userId: string;
  };
  export type WalletMultiReleaseResponse = {
    /** النجاح */
    success: boolean;
    /** هل تم الحفظ بالفعل */
    alreadyReleased: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletMultiReleaseBody = {
    /** معرفات الحفظ */
    multiHoldIds: string[];
  };
  export type WalletCapturePathParams = {
    /** معرف المستخدم */
    userId: string;
    /** معرف الحفظ */
    holdId: string;
  };
  export type WalletCaptureResponse = {
    /** النجاح */
    success: boolean;
    /** هل تم الحفظ بالفعل */
    alreadyCaptured: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletMultiCapturePathParams = {
    /** معرف المستخدم */
    userId: string;
  };
  export type WalletMultiCaptureResponse = {
    /** النجاح */
    success: boolean;
    /** هل تم الحفظ بالفعل */
    alreadyCaptured: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletMultiCaptureBody = {
    /** معرفات الحفظ */
    multiHoldIds: string[];
  };
  export type WalletRefundPathParams = {
    /** معرف المستخدم */
    userId: string;
    /** معرف الترخيص */
    captureId: string;
  };
  export type WalletRefundResponse = {
    /** النجاح */
    success: boolean;
    /** هل تم الترجيع بالفعل */
    alreadyRefunded: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletHoldResponse = {
    /** معرف الحفظ */
    holdId: string;
    /** النجاح */
    success: boolean;
    /** هل تم الحفظ بالفعل */
    alreadyHold: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletHoldPathParams = {
    /** معرف المستخدم */
    userId: string;
  };
  export type WalletHoldBody = {
    /** المبلغ */
    amount: number;
    /** معرف الشحنة */
    idempotencyKey: string;
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف الطلب الفرعي */
    subOrderId?: string | null;
    /** معرف المنظمة */
    orgId?: string | null;
  };
  export type WalletMultiHoldResponse = {
    /** معرفات الحفظ */
    multiHoldIds: string[];
    /** النجاح */
    success: boolean;
    /** هل تم الحفظ بالفعل */
    alreadyHold: boolean;
    /** السبب */
    failReason?: LanguageString | null;
  };
  export type WalletMultiHoldPathParams = {
    /** معرف المستخدم */
    userId: string;
  };
  export type WalletMultiHoldBody = {
    /** البيانات */
    multiHoldData: WalletHoldBody[];
  };
  export type WalletTopUpPathParams = {
    /** معرف المستخدم */
    userId: string;
  };
  export type WalletTopUpBody = {
    /** المبلغ */
    amount: Decimal;
    /** المبلغ بالدولار الأمريكي */
    usdAmount?: Decimal | null;
    /** معرف التكرار */
    idempotencyKey: string;
    /** نسبة التبادل */
    exchangeRate?: Decimal | null;
    /** طريقة الشحن */
    topUpPaymentMethod: Enums.TopUpPaymentMethod;
    /** المزود */
    provider: Enums.PaymentProvider;
    /** معرف نية المزود */
    providerIntentId: string;
  };
  export type PaymentIntentConfirmMetadata = {
    /** معلومات Moamalat */
    moamalat?: any | null;
    /** معلومات Masarat */
    masarat?: any | null;
  };
  export type PaymentIntentConfirmBody = {
    /** معرف نية الدفع */
    paymentIntentId: string;
    /** طريقة الشحن */
    topUpPaymentMethod: Enums.TopUpPaymentMethod;
    /** البيانات */
    metadata: PaymentIntentConfirmMetadata;
  };
  export type CreatePaymentIntentBody = {
    /** نوع الشحن */
    type: Enums.PaymentIntentType;
    /** المبلغ */
    amount: Decimal;
    /** طريقة الشحن */
    topUpPaymentMethod: Enums.TopUpPaymentMethod;
    /** معرف الطلب */
    orderId?: string | null;
    /** البطاقة الشخصية */
    identityCard?: string | null;
    /** جلسة Masarat */
    masaratSessionToken?: string | null;
  };
  export type PaymentIntentResponse = {
    /** رابط الدفع */
    checkoutUrl?: string | null;
    /** البيانات */
    moamalatMetadata?: any | null;
    /** معرف نية الدفع */
    paymentIntentId: string;
  };
  export type WalletTransactionsListResponse = {
    /** المعاملات */
    walletTransactions: WalletTransaction[];
    /** الترقيم */
    pagination: Pagination;
  };
  export type WalletTransactionsListQueryParams = {
    /** العدد الكلي لكل صفحة */
    totalPerPage: number;
    /** الصفحة */
    page: number;
    /** الترتيب */
    sort?: string | null;
    /** معرف المستخدم */
    userId?: string | null;
  };
  export type ExchangeRateResponse = {
    /** الدولار الأمريكي */
    usd: Decimal;
  };
  export type AddFCMTokenBody = {
    /** رمز FCM */
    token: string;
  };
  export type RemoveFCMTokenBody = {
    /** رمز FCM */
    token: string;
  };
  export type CategoryCreateBody = {
    /** الاسم */
    name: string;
  };
  export type VendorCreateBody = {
    /** تم الحذف */
    deleted: boolean;
    /** الاسم */
    name: string;
    /** العنوان - التصنيف */
    addressLabel?: string | null;
    /** العنوان - الشارع */
    addressStreet?: string | null;
    /** العنوان - الداخلي */
    addressInternal?: string | null;
    /** العنوان - الهاتف */
    addressPhone?: string | null;
    /** العنوان - الهاتف الثاني */
    addressPhone2?: string | null;
    /** العنوان - العرض */
    addressLat?: number | null;
    /** العنوان - الطول */
    addressLng?: number | null;
    /** الشعار */
    logo?: string | null;
    /** الخلفية */
    background?: string | null;
  };
  export type VendorDeletePathParams = {
    /** معرف المتجر */
    vendorId: string;
  };
  export type VendorUpdatePathParams = {
    /** معرف المتجر */
    vendorId: string;
  };
  export type VendorUpdateBody = {
    /** البيانات */
    vendor: VendorUpdate;
  };
  export type ProductDeletePathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type ProductUpdatePathParams = {
    /** معرف المنتج */
    productId: string;
  };
  export type ProductUpdateBody = {
    /** البيانات */
    product: ProductUpdate;
  };
  export type ProductCreatePathParams = {
    /** معرف المتجر */
    vendorId: string;
  };
  export type ProductCreateBody = {
    /** الحجم */
    size: Enums.ProductSize;
    /** الاسم */
    name: string;
    /** الوصف */
    description?: string | null;
    /** الصور */
    images?: string[] | null;
    /** السعر */
    price?: number | null;
    /** السعر */
    discount?: number | null;
    /** الكمية */
    stock?: number | null;
    /** الخصائص */
    variations?: ProductVariation[] | null;
    /** الخلفية */
    bgColor?: string | null;
  };
  export type ProductReviewCreateBody = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** المنتج */
    productId: string;
    /** التقييم */
    rating: number;
    /** المراجعة */
    review?: string | null;
  };
  export type NewOrderJobData = {
    /** معرف الطلب */
    orderId: string;
    /** المستخدم */
    userId: string;
    /** المضيف */
    host?: string | null;
    /** معرف الشحنة */
    idempotencyKey: string;
  };
  export type NewVoucherBuyNowOrderJobData = {
    /** معرف الطلب */
    orderId: string;
    /** اللغة */
    lang: string;
    /** معرف الخصائص */
    variationId: string;
    /** معرف المنتج */
    productId: string;
    /** معرف الشحنة */
    idempotencyKey: string;
    /** المستخدم */
    userId: string;
    /** المضيف */
    host?: string | null;
  };
  export type NewSubOrderJobData = {
    /** المستخدم */
    userId: string;
    /** معرف الطلب */
    orderId: string;
    /** المضيف */
    host?: string | null;
    /** معرف الطلب الفرعي */
    subOrderId: string;
    /** معرف الشحنة */
    idempotencyKey: string;
  };
  export type WalletTopUpJobData = {
    /** المبلغ */
    amount: Decimal;
    /** المبلغ بالدولار الأمريكي */
    usdAmount?: Decimal | null;
    /** نسبة التبادل */
    exchangeRate?: Decimal | null;
    /** طريقة الشحن */
    topUpPaymentMethod: Enums.TopUpPaymentMethod;
    /** المزود */
    provider: Enums.PaymentProvider;
    /** معرف نية المزود */
    providerIntentId: string;
    /** معرف الشحنة */
    intentId: string;
    /** معرف الشحنة */
    idempotencyKey: string;
    /** المستخدم */
    userId: string;
  };
  export type BuyCardInstantlyJobData = {
    /** معرف البطاقة */
    id: string;
    /** المبلغ */
    amount: number;
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type NotificationData = {
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف الطلب الفرعي */
    subOrderId?: string | null;
  };
  export type SendCustomerNotificationJobData = {
    /** المستخدم */
    userId: string;
    /** العنوان */
    title: string;
    /** النص */
    body: string;
    /** النوع */
    type: string;
    /** المضيف */
    host?: string | null;
    /** البيانات */
    notificationData: NotificationData;
    /** كتم */
    silent?: boolean | null;
  };
  export type SendVendorNotificationData = {
    /** معرف الطلب */
    orderId?: string | null;
    /** معرف الطلب الفرعي */
    subOrderId?: string | null;
  };
  export type SendVendorNotificationJobData = {
    /** المنظمة */
    orgId: string;
    /** العنوان */
    title: string;
    /** النص */
    body: string;
    /** النوع */
    type: string;
    /** المضيف */
    host?: string | null;
    /** البيانات */
    notificationData: SendVendorNotificationData;
    /** كتم */
    silent?: boolean | null;
  };
  export type CreateExternalShippingOrderJobData = {
    /** معرف الشحنة */
    shippingOrderId: string;
  };
  export type CreateShippingOrderJobData = {
    /** معرف الطلب */
    orderId: string;
    /** معرف الطلب الفرعي */
    subOrderId: string;
  };
  export type ProductModerationJobData = {
    /** المنتج */
    productId: string;
  };
  export type VendorModerationJobData = {
    /** المتجر */
    vendorId: string;
  };
  export type BlurHashProductImageJobData = {
    /** المنتج */
    product?: ProductBlurHash | null;
    /** المتجر */
    vendor?: VendorBlurHash | null;
  };
  export type ProductBlurHash = {
    /** معرف المنتج */
    productId: string;
    /** رابط الصورة */
    imageUrl: string;
  };
  export type VendorBlurHash = {
    /** معرف المتجر */
    vendorId: string;
    /** رابط الشعار */
    logoUrl: string;
    /** رابط الخلفية */
    backgroundUrl: string;
  };
}

export function deNull<T extends z.core.SomeType>(schema: T) {
  return z.preprocess((val) => (val == null ? undefined : val), schema);
}

export function deNullString<T extends z.core.SomeType>(schema: T) {
  return z.preprocess((val) => {
    if (val == null) return undefined;
    if (typeof val !== 'string') return val;
    const trimmed = val.trim();
    return trimmed === '' ? undefined : trimmed;
  }, schema);
}

export function paginationSchema() {
  return z.object({
    /** الصفحة */
    page: z.number(),
  });
}

export function diffOperationSchema() {
  return z.object({
    /** op */
    op: z.enum(DiffOpList),
    /** path */
    path: z.string(),
    /** value */
    value: z.any().nullish(),
    /** from */
    from: z.string().nullish(),
  });
}

export function diffOperationCreateSchema() {
  return z.object({
    /** op */
    op: z.enum(DiffOpList),
    /** path */
    path: z.string(),
    /** value */
    value: z.any().nullish(),
    /** from */
    from: z.string().nullish(),
  });
}

export function diffOperationUpdateSchema() {
  return z.object({
    /** op */
    op: z.enum(DiffOpList).nullish(),
    /** path */
    path: z.string().nullish(),
    /** value */
    value: z.any().nullish(),
    /** from */
    from: z.string().nullish(),
  });
}

export function languageStringSchema() {
  return z.object({
    /** الإنجليزية */
    en: z.string(),
    /** العربية */
    ar: z.string(),
  });
}

export function languageStringCreateSchema() {
  return z.object({
    /** الإنجليزية */
    en: z.string(),
    /** العربية */
    ar: z.string(),
  });
}

export function languageStringUpdateSchema() {
  return z.object({
    /** الإنجليزية */
    en: z.string().nullish(),
    /** العربية */
    ar: z.string().nullish(),
  });
}

export function geoLocationSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** lat */
    latitude: z.number().nullish(),
    /** lon */
    longitude: z.number().nullish(),
    /** name */
    name: z.string().nullish(),
    /** displayName */
    displayName: z.string().nullish(),
    /** type */
    type: z.string().nullish(),
    /** addressType */
    addressType: z.string().nullish(),
    /** importance */
    importance: z.number().nullish(),
    /** placeRank */
    placeRank: z.number().nullish(),
    /** noResult */
    noResult: z.boolean(),
    /** query */
    query: z.string(),
    /** shippingCompany */
    shippingCompany: z.enum(ShippingCompanyList),
    /** location */
    location: shippingExternalLocationSchema(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function geoLocationCreateSchema() {
  return z.object({
    /** lat */
    latitude: z.number().nullish(),
    /** lon */
    longitude: z.number().nullish(),
    /** name */
    name: z.string().nullish(),
    /** displayName */
    displayName: z.string().nullish(),
    /** type */
    type: z.string().nullish(),
    /** addressType */
    addressType: z.string().nullish(),
    /** importance */
    importance: z.number().nullish(),
    /** placeRank */
    placeRank: z.number().nullish(),
    /** noResult */
    noResult: z.boolean(),
    /** query */
    query: z.string(),
    /** shippingCompany */
    shippingCompany: z.enum(ShippingCompanyList),
    /** location */
    location: shippingExternalLocationSchema(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function geoLocationUpdateSchema() {
  return z.object({
    /** lat */
    latitude: z.number().nullish(),
    /** lon */
    longitude: z.number().nullish(),
    /** name */
    name: z.string().nullish(),
    /** displayName */
    displayName: z.string().nullish(),
    /** type */
    type: z.string().nullish(),
    /** addressType */
    addressType: z.string().nullish(),
    /** importance */
    importance: z.number().nullish(),
    /** placeRank */
    placeRank: z.number().nullish(),
    /** noResult */
    noResult: z.boolean().nullish(),
    /** query */
    query: z.string().nullish(),
    /** shippingCompany */
    shippingCompany: z.enum(ShippingCompanyList).nullish(),
    /** location */
    location: shippingExternalLocationSchema().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function shippingExternalLocationSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** المدينة */
    cityId: z.string(),
    /** المدينة */
    cityName: z.string(),
    /** المنطقة */
    regionId: z.string(),
    /** المنطقة */
    regionName: z.string(),
    /** المنطقة مطلوبة */
    regionRequired: z.boolean().nullish(),
    /** مفقود */
    missing: z.boolean(),
    /** شركة الشحن */
    shippingCompany: z.enum(ShippingCompanyList),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function shippingExternalLocationCreateSchema() {
  return z.object({
    /** المدينة */
    cityId: z.string(),
    /** المدينة */
    cityName: z.string(),
    /** المنطقة */
    regionId: z.string(),
    /** المنطقة */
    regionName: z.string(),
    /** المنطقة مطلوبة */
    regionRequired: z.boolean().nullish(),
    /** مفقود */
    missing: z.boolean(),
    /** شركة الشحن */
    shippingCompany: z.enum(ShippingCompanyList),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function shippingExternalLocationUpdateSchema() {
  return z.object({
    /** المدينة */
    cityId: z.string().nullish(),
    /** المدينة */
    cityName: z.string().nullish(),
    /** المنطقة */
    regionId: z.string().nullish(),
    /** المنطقة */
    regionName: z.string().nullish(),
    /** المنطقة مطلوبة */
    regionRequired: z.boolean().nullish(),
    /** مفقود */
    missing: z.boolean().nullish(),
    /** شركة الشحن */
    shippingCompany: z.enum(ShippingCompanyList).nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function vendorSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** تم الحذف */
    deleted: z.boolean(),
    /** المستخدم */
    userId: z.string(),
    /** المنظمة */
    orgId: z.string(),
    /** الاسم */
    name: z.string(),
    /** العنوان - التصنيف */
    addressLabel: z.string().nullish(),
    /** العنوان - الشارع */
    addressStreet: z.string().nullish(),
    /** العنوان - الداخلي */
    addressInternal: z.string().nullish(),
    /** العنوان - الهاتف */
    addressPhone: z.string().nullish(),
    /** العنوان - الهاتف الثاني */
    addressPhone2: z.string().nullish(),
    /** العنوان - العرض */
    addressLat: z.number().nullish(),
    /** العنوان - الطول */
    addressLng: z.number().nullish(),
    /** الشعار */
    logo: z.string().nullish(),
    /** blurHash */
    logoBlurHash: z.string().nullish(),
    /** الخلفية */
    background: z.string().nullish(),
    /** blurHash */
    backgroundBlurHash: z.string().nullish(),
    /** الحالة */
    publishStatus: z.enum(PublishStatusList),
    /** تمت الموافقة */
    approved: z.boolean(),
    /** تم التحقق */
    verified: z.boolean(),
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
    /** طرق الدفع المسموح بها */
    allowedPaymentMethods: z.enum(PaymentMethodList).array(),
    /** التسلسل */
    seq: z.number(),
    /** التسلسل */
    subOrderSequence: z.number(),
    /** نوع المتجر */
    vendorType: z.enum(VendorTypeList),
    /** مراجعة مزدوجة */
    doubleReview: z.boolean().nullish(),
    /** المراجعات */
    moderations: z.array(vendorModerationSchema()).nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function vendorCreateSchema() {
  return z.object({
    /** تم الحذف */
    deleted: z.boolean(),
    /** الاسم */
    name: z.string(),
    /** العنوان - التصنيف */
    addressLabel: z.string().nullish(),
    /** العنوان - الشارع */
    addressStreet: z.string().nullish(),
    /** العنوان - الداخلي */
    addressInternal: z.string().nullish(),
    /** العنوان - الهاتف */
    addressPhone: z.string().nullish(),
    /** العنوان - الهاتف الثاني */
    addressPhone2: z.string().nullish(),
    /** العنوان - العرض */
    addressLat: z.number().nullish(),
    /** العنوان - الطول */
    addressLng: z.number().nullish(),
    /** الشعار */
    logo: z.string().nullish(),
    /** الخلفية */
    background: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function vendorUpdateSchema() {
  return z.object({
    /** تم الحذف */
    deleted: z.boolean().nullish(),
    /** الاسم */
    name: z.string().nullish(),
    /** العنوان - التصنيف */
    addressLabel: z.string().nullish(),
    /** العنوان - الشارع */
    addressStreet: z.string().nullish(),
    /** العنوان - الداخلي */
    addressInternal: z.string().nullish(),
    /** العنوان - الهاتف */
    addressPhone: z.string().nullish(),
    /** العنوان - الهاتف الثاني */
    addressPhone2: z.string().nullish(),
    /** العنوان - العرض */
    addressLat: z.number().nullish(),
    /** العنوان - الطول */
    addressLng: z.number().nullish(),
    /** الشعار */
    logo: z.string().nullish(),
    /** الخلفية */
    background: z.string().nullish(),
    /** طرق الدفع المسموح بها */
    allowedPaymentMethods: z.enum(PaymentMethodList).array().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function chatMessageSchema() {
  return z.object({
    /** معرف */
    id: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** نوع المستخدم */
    userType: z.enum(UserTypeList),
    /** الاسم */
    userName: z.string().nullish(),
    /** النص */
    text: z.string(),
    /** المرفق */
    attachment: z.string().nullish(),
    /** القراءات */
    reads: z.array(chatMessageReadSchema()).nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function chatMessageCreateSchema() {
  return z.object({
    /** النص */
    text: z.string(),
    /** المرفق */
    attachment: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function chatMessageUpdateSchema() {
  return z.object({
    /** النص */
    text: z.string().nullish(),
    /** المرفق */
    attachment: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function chatMessageReadSchema() {
  return z.object({
    /** معرف الرسالة */
    messageId: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** تاريخ القراءة */
    readAt: z.coerce.date(),
  });
}

export function chatMessageReadCreateSchema() {
  return z.object({
    /** معرف الرسالة */
    messageId: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** تاريخ القراءة */
    readAt: z.coerce.date(),
  });
}

export function chatMessageReadUpdateSchema() {
  return z.object({
    /** معرف الرسالة */
    messageId: z.string().nullish(),
    /** المستخدم */
    userId: z.string().nullish(),
    /** تاريخ القراءة */
    readAt: z.coerce.date().nullish(),
  });
}

export function vendorModerationSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** معرف المتجر */
    vendorId: z.string(),
    /** القرار */
    decision: z.enum(ModerationDecisionList),
    /** تمت المراجعة بواسطة */
    reviewedById: z.string().nullish(),
    /** تاريخ المراجعة */
    reviewedAt: z.coerce.date().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    /** فحص الشعار */
    logoCheck: z.any().nullish(),
    /** فحص الخلفية */
    backgroundCheck: z.any().nullish(),
    /** فحص الألفاظ النابية */
    profanityCheck: z.any().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function vendorModerationCreateSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string(),
    /** القرار */
    decision: z.enum(ModerationDecisionList),
    /** تمت المراجعة بواسطة */
    reviewedById: z.string().nullish(),
    /** تاريخ المراجعة */
    reviewedAt: z.coerce.date().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    /** فحص الشعار */
    logoCheck: z.any().nullish(),
    /** فحص الخلفية */
    backgroundCheck: z.any().nullish(),
    /** فحص الألفاظ النابية */
    profanityCheck: z.any().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function vendorModerationUpdateSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string().nullish(),
    /** القرار */
    decision: z.enum(ModerationDecisionList).nullish(),
    /** تمت المراجعة بواسطة */
    reviewedById: z.string().nullish(),
    /** تاريخ المراجعة */
    reviewedAt: z.coerce.date().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    /** فحص الشعار */
    logoCheck: z.any().nullish(),
    /** فحص الخلفية */
    backgroundCheck: z.any().nullish(),
    /** فحص الألفاظ النابية */
    profanityCheck: z.any().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productModerationSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** معرف المنتج */
    productId: z.string(),
    /** القرار */
    decision: z.enum(ModerationDecisionList),
    /** تمت المراجعة بواسطة */
    reviewedById: z.string().nullish(),
    /** تاريخ المراجعة */
    reviewedAt: z.coerce.date().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    /** فحص النص */
    textCheck: z.any().nullish(),
    /** فحوصات الصور */
    imageChecks: z.any().nullish(),
    /** فحص الألفاظ النابية */
    profanityCheck: z.any().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productModerationCreateSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
    /** القرار */
    decision: z.enum(ModerationDecisionList),
    /** تمت المراجعة بواسطة */
    reviewedById: z.string().nullish(),
    /** تاريخ المراجعة */
    reviewedAt: z.coerce.date().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    /** فحص النص */
    textCheck: z.any().nullish(),
    /** فحوصات الصور */
    imageChecks: z.any().nullish(),
    /** فحص الألفاظ النابية */
    profanityCheck: z.any().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productModerationUpdateSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string().nullish(),
    /** القرار */
    decision: z.enum(ModerationDecisionList).nullish(),
    /** تمت المراجعة بواسطة */
    reviewedById: z.string().nullish(),
    /** تاريخ المراجعة */
    reviewedAt: z.coerce.date().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    /** فحص النص */
    textCheck: z.any().nullish(),
    /** فحوصات الصور */
    imageChecks: z.any().nullish(),
    /** فحص الألفاظ النابية */
    profanityCheck: z.any().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function subOrderStatusEventSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** الحالة */
    status: z.enum(SubOrderStatusList),
    /** السبب بالإنجليزية */
    reasonEn: z.string().nullish(),
    /** السبب بالعربية */
    reasonAr: z.string().nullish(),
    /** تم التغيير بواسطة */
    changedById: z.string().nullish(),
  });
}

export function subOrderStatusEventCreateSchema() {
  return z.object({
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** الحالة */
    status: z.enum(SubOrderStatusList),
    /** السبب بالإنجليزية */
    reasonEn: z.string().nullish(),
    /** السبب بالعربية */
    reasonAr: z.string().nullish(),
    /** تم التغيير بواسطة */
    changedById: z.string().nullish(),
  });
}

export function subOrderStatusEventUpdateSchema() {
  return z.object({
    /** معرف الطلب الفرعي */
    subOrderId: z.string().nullish(),
    /** الحالة */
    status: z.enum(SubOrderStatusList).nullish(),
    /** السبب بالإنجليزية */
    reasonEn: z.string().nullish(),
    /** السبب بالعربية */
    reasonAr: z.string().nullish(),
    /** تم التغيير بواسطة */
    changedById: z.string().nullish(),
  });
}

export function orderSchema() {
  return z.object({
    /** رقم الطلب */
    orderNo: z.string(),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
    /**  */
    id: z.string(),
    /** تاريخ الطلب */
    orderDate: z.coerce.date(),
    /** المستخدم */
    userId: z.string(),
    /** الاجمالي */
    total: z.number(),
    /** الاجمالي */
    subTotal: z.number(),
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
    /** معرف عنوان الشحن */
    shippingAddressId: z.string().nullish(),
    /** العنوان - التصنيف */
    addressLabel: z.string().nullish(),
    /** العنوان - الشارع */
    addressStreet: z.string(),
    /** العنوان - الداخلي */
    addressInternal: z.string().nullish(),
    /** العنوان - الهاتف */
    addressPhone: z.string(),
    /** العنوان - الهاتف الثاني */
    addressPhone2: z.string().nullish(),
    /** العنوان - العرض */
    addressLat: z.number(),
    /** العنوان - الطول */
    addressLng: z.number(),
    /** الطلبات الفرعية */
    subOrders: z.array(subOrderSchema()),
    /** الحالة */
    status: z.enum(OrderStatusList),
    /** طريقة الدفع */
    paymentMethod: z.enum(PaymentMethodList),
    /** خارجية */
    external: z.boolean().nullish(),
    /** الملاحظات */
    notes: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function orderCreateSchema() {
  return z.object({
    /** معرف الشحنة */
    idempotencyKey: z.string(),
    /** العنوان - التصنيف */
    addressLabel: z.string().nullish(),
    /** العنوان - الشارع */
    addressStreet: z.string(),
    /** العنوان - الداخلي */
    addressInternal: z.string().nullish(),
    /** العنوان - الهاتف */
    addressPhone: z.string(),
    /** العنوان - الهاتف الثاني */
    addressPhone2: z.string().nullish(),
    /** العنوان - العرض */
    addressLat: z.number(),
    /** العنوان - الطول */
    addressLng: z.number(),
    /** طريقة الدفع */
    paymentMethod: z.enum(PaymentMethodList),
    /** الملاحظات */
    notes: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function subOrderSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** معرف الحفظ */
    walletHoldId: z.string().nullish(),
    /** المستخدم */
    userId: z.string(),
    /** المنظمة */
    orgId: z.string(),
    /** المنتجات */
    orderProducts: z.array(orderProductSchema()),
    /** الاجمالي */
    total: z.number(),
    /** الاجمالي */
    subTotal: z.number(),
    /** الحالة */
    status: z.enum(SubOrderStatusList),
    /** الدردشة */
    chatMessages: z.array(chatMessageSchema()).nullish(),
    /** أحداث الحالة */
    statusEvents: z.array(subOrderStatusEventSchema()).nullish(),
    /** الملاحظات الإنجليزية */
    notesEn: z.string().nullish(),
    /** الملاحظات العربية */
    notesAr: z.string().nullish(),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
  });
}

export function subOrderCreateSchema() {
  return z.object({
    /** المنتجات */
    orderProducts: z.array(orderProductSchema()),
    /** الملاحظات الإنجليزية */
    notesEn: z.string().nullish(),
    /** الملاحظات العربية */
    notesAr: z.string().nullish(),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
  });
}

export function subOrderUpdateSchema() {
  return z.object({
    /** المنتجات */
    orderProducts: z.array(orderProductSchema()).nullish(),
    /** الملاحظات الإنجليزية */
    notesEn: z.string().nullish(),
    /** الملاحظات العربية */
    notesAr: z.string().nullish(),
    /** معرف الشحنة */
    idempotencyKey: z.string().nullish(),
  });
}

export function paymentIntentMetaDataSchema() {
  return z.object({
    /** معرف المعاملة */
    transactionId: z.string().nullish(),
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** معرف العميل */
    customerId: z.string().nullish(),
    /** بريد العميل */
    customerEmail: z.string().nullish(),
  });
}

export function paymentIntentMetaDataCreateSchema() {
  return z.object({
    /** معرف المعاملة */
    transactionId: z.string().nullish(),
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** معرف العميل */
    customerId: z.string().nullish(),
    /** بريد العميل */
    customerEmail: z.string().nullish(),
  });
}

export function paymentIntentMetaDataUpdateSchema() {
  return z.object({
    /** معرف المعاملة */
    transactionId: z.string().nullish(),
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** معرف العميل */
    customerId: z.string().nullish(),
    /** بريد العميل */
    customerEmail: z.string().nullish(),
  });
}

export function paymentIntentWebhookSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** الحالة */
    status: z.enum(WPayPaymentIntentStatusList),
    /** بيانات اضافية */
    metadata: paymentIntentMetaDataSchema(),
    /** المبلغ */
    amount: z.number(),
    /** رمل */
    sandbox: z.boolean(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productVariationMetadataSchema() {
  return z.object({
    /** MNO */
    voucherMno: z.string().nullish(),
    /** كود المنتج MNO */
    voucherProductCode: z.string().nullish(),
    /** المبلغ */
    voucherAmount: z.number().nullish(),
  });
}

export function productVariationMetadataCreateSchema() {
  return z.object({
    /** MNO */
    voucherMno: z.string().nullish(),
    /** كود المنتج MNO */
    voucherProductCode: z.string().nullish(),
    /** المبلغ */
    voucherAmount: z.number().nullish(),
  });
}

export function productVariationMetadataUpdateSchema() {
  return z.object({
    /** MNO */
    voucherMno: z.string().nullish(),
    /** كود المنتج MNO */
    voucherProductCode: z.string().nullish(),
    /** المبلغ */
    voucherAmount: z.number().nullish(),
  });
}

export function productVariationSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** الصورة */
    image: z.string().nullish(),
    /** السعر */
    price: z.number(),
    /** الخصم */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number(),
    /** الخصائص */
    attributes: z.array(variationAttributeSchema()),
    /** البيانات الوصفية */
    metadata: productVariationMetadataSchema().nullish(),
  });
}

export function productVariationCreateSchema() {
  return z.object({
    /** الصورة */
    image: z.string().nullish(),
    /** السعر */
    price: z.number(),
    /** الخصم */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number(),
    /** الخصائص */
    attributes: z.array(variationAttributeSchema()),
    /** البيانات الوصفية */
    metadata: productVariationMetadataSchema().nullish(),
  });
}

export function productVariationUpdateSchema() {
  return z.object({
    /** الصورة */
    image: z.string().nullish(),
    /** السعر */
    price: z.number().nullish(),
    /** الخصم */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number().nullish(),
    /** الخصائص */
    attributes: z.array(variationAttributeSchema()).nullish(),
    /** البيانات الوصفية */
    metadata: productVariationMetadataSchema().nullish(),
  });
}

export function orderProductSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** معرف المنتج */
    productId: z.string(),
    /** معرف الخصائص */
    variationId: z.string().nullish(),
    /** اسم المنتج */
    productName: z.string(),
    /** صورة المنتج */
    productImage: z.string().nullish(),
    /** نوع المنتج */
    productType: z.enum(ProductTypeList),
    /** حجم المنتج */
    productSize: z.enum(ProductSizeList),
    /** معرف التصنيف */
    categoryId: z.string(),
    /** اسم التصنيف */
    categoryName: z.string(),
    /** معرف المتجر */
    vendorId: z.string(),
    /** اسم المتجر */
    vendorName: z.string(),
    /** شعار المتجر */
    vendorLogo: z.string().nullish(),
    /** صورة الخصائص */
    variationImage: z.string().nullish(),
    /** خصائص المتغير */
    variationAttributes: z.any().nullish(),
    /** الكمية */
    quantity: z.number(),
    /** السعر النهائي */
    finalPrice: z.number(),
    /** بيانات المنتج */
    metadata: orderProductMetaDataSchema().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function orderProductCreateSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
    /** معرف الخصائص */
    variationId: z.string().nullish(),
    /** اسم المنتج */
    productName: z.string(),
    /** صورة المنتج */
    productImage: z.string().nullish(),
    /** نوع المنتج */
    productType: z.enum(ProductTypeList),
    /** حجم المنتج */
    productSize: z.enum(ProductSizeList),
    /** معرف التصنيف */
    categoryId: z.string(),
    /** اسم التصنيف */
    categoryName: z.string(),
    /** معرف المتجر */
    vendorId: z.string(),
    /** اسم المتجر */
    vendorName: z.string(),
    /** شعار المتجر */
    vendorLogo: z.string().nullish(),
    /** صورة الخصائص */
    variationImage: z.string().nullish(),
    /** خصائص المتغير */
    variationAttributes: z.any().nullish(),
    /** الكمية */
    quantity: z.number(),
    /** السعر النهائي */
    finalPrice: z.number(),
    /** بيانات المنتج */
    metadata: orderProductMetaDataSchema().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function orderProductMetaDataSchema() {
  return z.object({
    /** الكوبون */
    voucher: voucherSchema().nullish(),
  });
}

export function orderProductMetaDataCreateSchema() {
  return z.object({
    /** الكوبون */
    voucher: voucherSchema().nullish(),
  });
}

export function orderProductMetaDataUpdateSchema() {
  return z.object({
    /** الكوبون */
    voucher: voucherSchema().nullish(),
  });
}

export function categorySchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** الاسم */
    name: z.string(),
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function categoryCreateSchema() {
  return z.object({
    /** الاسم */
    name: z.string(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function categoryUpdateSchema() {
  return z.object({
    /** الاسم */
    name: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function numberRangeSchema() {
  return z.object({
    /** الحد الأدنى */
    min: z.number(),
    /** الحد الأقصى */
    max: z.number(),
  });
}

export function numberRangeCreateSchema() {
  return z.object({
    /** الحد الأدنى */
    min: z.number(),
    /** الحد الأقصى */
    max: z.number(),
  });
}

export function numberRangeUpdateSchema() {
  return z.object({
    /** الحد الأدنى */
    min: z.number().nullish(),
    /** الحد الأقصى */
    max: z.number().nullish(),
  });
}

export function voucherSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** تم البيع */
    locked: z.boolean(),
    /** كود MNO */
    mnoCode: z.string().nullish(),
    /** المبلغ */
    amount: z.number().nullish(),
    /** الرقم */
    sN: z.string().nullish(),
    /** الكود */
    code: z.string().nullish(),
    /** تاريخ الانتهاء */
    expiryDate: z.coerce.date().nullish(),
    /** المنتج */
    product: z.string().nullish(),
    /** كود المنتج */
    productCode: z.string().nullish(),
    /** نوع MNO */
    mnoType: z.string().nullish(),
    /** السعر */
    price: z.number().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** تم الحذف */
    deleted: z.boolean(),
    /** الحجم */
    size: z.enum(ProductSizeList),
    /** المستخدم */
    userId: z.string(),
    /** المنظمة */
    orgId: z.string(),
    /** الاسم */
    name: z.string(),
    /** الوصف */
    description: z.string().nullish(),
    /** الصور */
    images: z.array(z.string()).nullish(),
    /** السعر */
    price: z.number().nullish(),
    /** السعر */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number().nullish(),
    /** عدد التقييمات */
    ratingCount: z.number(),
    /** متوسط التقييمات */
    ratingAvg: z.number().nullish(),
    /** الحالة */
    publishStatus: z.enum(PublishStatusList),
    /** ملاحظات النشر */
    publishNotes: z.string().nullish(),
    /** المتجر */
    vendor: vendorSchema(),
    /** المتجر */
    vendorId: z.string(),
    /** التصنيف */
    category: categorySchema(),
    /** الخصائص */
    variations: z.array(productVariationSchema()).nullish(),
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
    /** الخلفية */
    bgColor: z.string().nullish(),
    /** النوع */
    type: z.enum(ProductTypeList),
    /** blurHash */
    blurHash: z.string().nullish(),
    /** مراجعة مزدوجة */
    doubleReview: z.boolean().nullish(),
    /** المراجعات */
    moderations: z.array(productModerationSchema()).nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productCreateSchema() {
  return z.object({
    /** الحجم */
    size: z.enum(ProductSizeList),
    /** الاسم */
    name: z.string(),
    /** الوصف */
    description: z.string().nullish(),
    /** الصور */
    images: z.array(z.string()).nullish(),
    /** السعر */
    price: z.number().nullish(),
    /** السعر */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number().nullish(),
    /** الخصائص */
    variations: z.array(productVariationSchema()).nullish(),
    /** الخلفية */
    bgColor: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productUpdateSchema() {
  return z.object({
    /** الحجم */
    size: z.enum(ProductSizeList).nullish(),
    /** الاسم */
    name: z.string().nullish(),
    /** الوصف */
    description: z.string().nullish(),
    /** الصور */
    images: z.array(z.string()).nullish(),
    /** السعر */
    price: z.number().nullish(),
    /** السعر */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number().nullish(),
    /** الخصائص */
    variations: z.array(productVariationSchema()).nullish(),
    /** الخلفية */
    bgColor: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function variationAttributeSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** النوع العنوان */
    typeLabel: z.string(),
    /** النوع المفتاح */
    typeKey: z.string(),
    /** النوع */
    type: z.enum(AttributeTypeList),
    /** العنوان */
    label: z.string(),
    /** المفتاح */
    key: z.string(),
  });
}

export function variationAttributeCreateSchema() {
  return z.object({
    /** النوع العنوان */
    typeLabel: z.string(),
    /** النوع المفتاح */
    typeKey: z.string(),
    /** النوع */
    type: z.enum(AttributeTypeList),
    /** العنوان */
    label: z.string(),
    /** المفتاح */
    key: z.string(),
  });
}

export function variationAttributeUpdateSchema() {
  return z.object({
    /** النوع العنوان */
    typeLabel: z.string().nullish(),
    /** النوع المفتاح */
    typeKey: z.string().nullish(),
    /** النوع */
    type: z.enum(AttributeTypeList).nullish(),
    /** العنوان */
    label: z.string().nullish(),
    /** المفتاح */
    key: z.string().nullish(),
  });
}

export function attachmentSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** الوصف */
    description: z.string().nullish(),
    /** الرابط */
    url: z.string(),
    /** المفتاح */
    key: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** المنظمة */
    orgId: z.string(),
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function attachmentCreateSchema() {
  return z.object({
    /** الوصف */
    description: z.string().nullish(),
    /** الرابط */
    url: z.string(),
    /** المفتاح */
    key: z.string(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function attachmentUpdateSchema() {
  return z.object({
    /** الوصف */
    description: z.string().nullish(),
    /** الرابط */
    url: z.string().nullish(),
    /** المفتاح */
    key: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productReviewSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** الاسم */
    userName: z.string(),
    /** المنظمة */
    orgId: z.string(),
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** المنتج */
    productId: z.string(),
    /** التقييم */
    rating: z.number(),
    /** المراجعة */
    review: z.string().nullish(),
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productReviewCreateSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** المنتج */
    productId: z.string(),
    /** التقييم */
    rating: z.number(),
    /** المراجعة */
    review: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function productReviewUpdateSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string().nullish(),
    /** المنتج */
    productId: z.string().nullish(),
    /** التقييم */
    rating: z.number().nullish(),
    /** المراجعة */
    review: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function addressSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** التصنيف */
    label: z.string(),
    /** الشارع */
    street: z.string(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number(),
    /** الطول */
    longitude: z.number(),
  });
}

export function addressCreateSchema() {
  return z.object({
    /** التصنيف */
    label: z.string(),
    /** الشارع */
    street: z.string(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number(),
    /** الطول */
    longitude: z.number(),
  });
}

export function addressUpdateSchema() {
  return z.object({
    /** التصنيف */
    label: z.string().nullish(),
    /** الشارع */
    street: z.string().nullish(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string().nullish(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number().nullish(),
    /** الطول */
    longitude: z.number().nullish(),
  });
}

export function userAddressSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** التصنيف */
    label: z.string(),
    /** الشارع */
    street: z.string(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number(),
    /** الطول */
    longitude: z.number(),
    /** العنوان الافتراضي */
    isDefault: z.boolean(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function userAddressCreateSchema() {
  return z.object({
    /** التصنيف */
    label: z.string(),
    /** الشارع */
    street: z.string(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number(),
    /** الطول */
    longitude: z.number(),
    /** العنوان الافتراضي */
    isDefault: z.boolean(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function userAddressUpdateSchema() {
  return z.object({
    /** التصنيف */
    label: z.string().nullish(),
    /** الشارع */
    street: z.string().nullish(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string().nullish(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number().nullish(),
    /** الطول */
    longitude: z.number().nullish(),
    /** العنوان الافتراضي */
    isDefault: z.boolean().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function userSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /**  */
    authId: z.string(),
    /**  */
    name: z.string().nullish(),
    /**  */
    email: z.string().nullish(),
    /**  */
    phone: z.string().nullish(),
    /**  */
    avatar: z.string().nullish(),
    /** مدير */
    isAdmin: z.boolean(),
    /** المنظمة */
    orgId: z.string(),
    /**  */
    birthdate: z.coerce.date().nullish(),
    /** العناوين */
    addresses: z.array(userAddressSchema()).nullish(),
    /** معرفات المتاجر */
    vendorIds: z.array(z.string()).nullish(),
    /** رموز FCM */
    customerFcmTokens: z.array(z.string()).nullish(),
    /** رموز FCM */
    adminFcmTokens: z.array(z.string()).nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function userUpdateSchema() {
  return z.object({
    /**  */
    authId: z.string().nullish(),
    /**  */
    name: z.string().nullish(),
    /**  */
    phone: z.string().nullish(),
    /**  */
    avatar: z.string().nullish(),
    /** مدير */
    isAdmin: z.boolean().nullish(),
    /**  */
    birthdate: z.coerce.date().nullish(),
    /** معرفات المتاجر */
    vendorIds: z.array(z.string()).nullish(),
    /** رموز FCM */
    customerFcmTokens: z.array(z.string()).nullish(),
    /** رموز FCM */
    adminFcmTokens: z.array(z.string()).nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function walletTransactionSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** معرف المحفظة */
    walletId: z.string(),
    /** المبلغ */
    amount: decimalSchema,
    /** العملة */
    currency: z.string(),
    /** النوع */
    type: z.enum(WalletTransactionTypeList),
    /** الحالة */
    status: z.enum(WalletTransactionStatusList),
    /** معرف التكرار */
    idempotencyKey: z.string(),
    /** المزود */
    provider: z.string().nullish(),
    /** نية المزود */
    providerIntent: z.string().nullish(),
    /** تاريخ التأكيد */
    confirmedAt: z.coerce.date().nullish(),
    /** معرف المنظمة */
    orgId: z.string().nullish(),
    /** دفع المزود */
    providerPayout: z.string().nullish(),
    /** تاريخ المعالجة */
    processedAt: z.coerce.date().nullish(),
    /** المبلغ بالدولار الأمريكي */
    usdAmount: decimalSchema.nullish(),
    /** نسبة التبادل */
    exchangeRate: decimalSchema.nullish(),
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList).nullish(),
    /** معرف العملية */
    orderId: z.string().nullish(),
    /** معرف العملية الفرعي */
    subOrderId: z.string().nullish(),
    /** المستخدم */
    userId: z.string(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function walletTransactionUpdateSchema() {
  return z.object({
    /** معرف المحفظة */
    walletId: z.string().nullish(),
    /** المبلغ */
    amount: decimalSchema.nullish(),
    /** العملة */
    currency: z.string().nullish(),
    /** النوع */
    type: z.enum(WalletTransactionTypeList).nullish(),
    /** الحالة */
    status: z.enum(WalletTransactionStatusList).nullish(),
    /** معرف التكرار */
    idempotencyKey: z.string().nullish(),
    /** المزود */
    provider: z.string().nullish(),
    /** نية المزود */
    providerIntent: z.string().nullish(),
    /** تاريخ التأكيد */
    confirmedAt: z.coerce.date().nullish(),
    /** معرف المنظمة */
    orgId: z.string().nullish(),
    /** دفع المزود */
    providerPayout: z.string().nullish(),
    /** تاريخ المعالجة */
    processedAt: z.coerce.date().nullish(),
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList).nullish(),
    /** معرف العملية */
    orderId: z.string().nullish(),
    /** معرف العملية الفرعي */
    subOrderId: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function walletSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** الرصيد */
    balance: decimalSchema,
    /** الرصيد المحجوز */
    holdBalance: decimalSchema,
    /** fakeForTesting */
    fakeForTesting: z.boolean(),
  });
}

export function walletUpdateSchema() {
  return z.object({
    /** الرصيد */
    balance: decimalSchema.nullish(),
    /** الرصيد المحجوز */
    holdBalance: decimalSchema.nullish(),
  });
}

export function paymentIntentSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** نوع الشحن */
    type: z.enum(PaymentIntentTypeList),
    /** المستخدم */
    userId: z.string().nullish(),
    /** المبلغ */
    amount: decimalSchema,
    /** المبلغ بالدولار الأمريكي */
    usdAmount: decimalSchema.nullish(),
    /** نسبة التبادل */
    exchangeRate: decimalSchema.nullish(),
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList),
    /** رابط الدفع */
    checkoutUrl: z.string().nullish(),
    /** الحالة */
    status: z.enum(PaymentIntentStatusList),
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** البطاقة الشخصية */
    identityCard: z.string().nullish(),
    /** جلسة Masarat */
    masaratSessionToken: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function paymentIntentUpdateSchema() {
  return z.object({
    /** المبلغ */
    amount: decimalSchema.nullish(),
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList).nullish(),
    /** البطاقة الشخصية */
    identityCard: z.string().nullish(),
    /** جلسة Masarat */
    masaratSessionToken: z.string().nullish(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function sharedCartItemSchema() {
  return z.object({
    /** المنتج */
    productId: z.string(),
    /** المتغير */
    variantId: z.string().nullish(),
    /** الكمية */
    quantity: z.number(),
  });
}

export function sharedCartSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** المنتجات */
    items: z.array(sharedCartItemSchema()),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
  });
}

export function userWishlistSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** المستخدم */
    userId: z.string(),
    /** المنتج */
    productId: z.string(),
  });
}

export function wPayWebhookSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** الحمولة */
    payload: z.any().nullish(),
  });
}

export function shippingFeeSchema() {
  return z.object({
    /**  */
    id: z.string(),
    /** البيانات */
    data: z.any().nullish(),
  });
}

export function sharedCartBodySchema() {
  return z.object({
    /** المنتجات */
    items: z.array(sharedCartItemSchema()),
  });
}

export function shareCartResponseSchema() {
  return z.object({
    /** معرف التبادل */
    id: z.string(),
  });
}

export function getSharedCartPathParamsSchema() {
  return z.object({
    /** معرف التبادل */
    id: z.string(),
  });
}

export function getSharedCartItemSchema() {
  return z.object({
    /** المنتج */
    product: productSchema(),
    /** المتغير */
    variantId: z.string().nullish(),
    /** الكمية */
    quantity: z.number(),
  });
}

export function getSharedCartResponseSchema() {
  return z.object({
    /** المنتجات */
    items: z.array(getSharedCartItemSchema()),
  });
}

export function orderProductForShippingResponseSchema() {
  return z.object({
    /** التكلفة */
    cost: z.number(),
    /** مدة التسليم */
    deliveryTime: languageStringSchema(),
  });
}

export function orderProductForShippingCostSchema() {
  return z.object({
    /** حجم المنتج */
    productSize: z.enum(ProductSizeList),
    /** عنوان المتجر */
    vendorAddress: addressSchema(),
    /** الكمية */
    quantity: z.number(),
  });
}

export function calculateShippingCostBodySchema() {
  return z.object({
    /** المنتجات */
    orderProducts: z.array(orderProductForShippingCostSchema()),
    /** العنوان */
    customerAddress: addressSchema(),
  });
}

export function vendorGetProductByIdPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    id: z.string(),
  });
}

export function getProductByIdPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    id: z.string(),
  });
}

export function getSubOrderChatResponseSchema() {
  return z.object({
    /** الرسائل */
    chat: z.array(chatMessageSchema()),
  });
}

export function getSubOrderChatPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
  });
}

export function getOrderByIdPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    id: z.string(),
  });
}

export function productsListResponseSchema() {
  return z.object({
    /** المنتجات */
    products: z.array(productSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function productsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** معرف المتجر */
    vendorId: z.string().nullish(),
    /** search */
    search: z.string().nullish(),
    /** حالة النشر */
    publishStatus: z.enum(PublishStatusList).nullish(),
    /** السعر من */
    priceFrom: z.number().nullish(),
    /** السعر إلى */
    priceTo: z.number().nullish(),
    /** التصنيف */
    category: z.string().nullish(),
    /** التقييم */
    stars: z.number().nullish(),
  });
}

export function relatedProductsListPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function relatedProductsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
  });
}

export function relatedProductsListResponseSchema() {
  return z.object({
    /** المنتجات ذات الصلة */
    relatedProducts: z.array(productSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function productReviewsListPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function productReviewsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
  });
}

export function productReviewsListResponseSchema() {
  return z.object({
    /** مراجعات المنتج */
    productReviews: z.array(productReviewSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function requestToPublishProductPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function unPublishProductPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function requestToPublishVendorPathParamsSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string(),
  });
}

export function unPublishVendorPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    vendorId: z.string(),
  });
}

export function getVendorByIdQueryParamsSchema() {
  return z.object({
    /** حالة المتجر */
    withDraft: z.boolean().nullish(),
  });
}

export function getVendorByIdPathParamsSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string(),
  });
}

export function vendorsListResponseSchema() {
  return z.object({
    /** المتاجر */
    vendors: z.array(vendorSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function vendorsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** حالة المتجر */
    publishStatus: z.enum(PublishStatusList).nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function vendorsListForVendorsResponseSchema() {
  return z.object({
    /** المتاجر */
    vendors: z.array(vendorSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function vendorsListForVendorsQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** حالة المتجر */
    publishStatus: z.enum(PublishStatusList).nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function categoriesListResponseSchema() {
  return z.object({
    /** التصنيفات */
    categories: z.array(categorySchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function categoriesListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** معرف الأصل */
    parentId: z.string().nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function ordersListResponseSchema() {
  return z.object({
    /** الطلبات */
    orders: z.array(orderSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function ordersListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** order status */
    status: z.enum(SubOrderStatusList).nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function ordersListCustomersResponseSchema() {
  return z.object({
    /** الطلبات */
    orders: z.array(orderSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function ordersListOrdersListCustomersQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** order status */
    status: z.enum(SubOrderStatusList).nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function adminProductsListResponseSchema() {
  return z.object({
    /** المنتجات */
    products: z.array(productSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function adminProductsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** معرف المتجر */
    vendorId: z.string().nullish(),
    /** حالة النشر */
    publishStatus: z.enum(PublishStatusList).nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function adminProductsDecisionItemSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
    /** القرار */
    decision: z.enum(ProductsDecisionList),
    /** السبب */
    publishNotes: z.string().nullish(),
  });
}

export function adminProductsDecisionListSchema() {
  return z.object({
    /** العناصر */
    decisionList: z.array(adminProductsDecisionItemSchema()),
  });
}

export function adminVendorsListResponseSchema() {
  return z.object({
    /** المتاجر */
    vendors: z.array(vendorSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function adminVendorsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** حالة النشر */
    publishStatus: z.enum(PublishStatusList).nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function adminVendorsDecisionItemSchema() {
  return z.object({
    /** معرف المنتج */
    vendorId: z.string(),
    /** القرار */
    decision: z.enum(VendorsDecisionList),
    /** السبب */
    publishNotes: z.string().nullish(),
  });
}

export function adminVendorsDecisionListSchema() {
  return z.object({
    /** العناصر */
    decisionList: z.array(adminVendorsDecisionItemSchema()),
  });
}

export function markOrderAsReadyForShippingPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
  });
}

export function markOrderAsProcessingPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
  });
}

export function cancelOrderPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
  });
}

export function cancelOrderBodySchema() {
  return z.object({
    /** السبب */
    reason: z.string().nullish(),
  });
}

export function addToWishlistPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function removeFromWishlistPathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function wishListResponseSchema() {
  return z.object({
    /** المنتجات */
    products: z.array(productSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function wishListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function productsListByFilterResponseSchema() {
  return z.object({
    /** المنتجات */
    products: z.array(productSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function productsListByFilterQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** filter */
    filter: z.string().nullish(),
    /** search */
    search: z.string().nullish(),
  });
}

export function vendorsListByFilterResponseSchema() {
  return z.object({
    /** المتاجر */
    vendors: z.array(vendorSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function vendorsListByFilterQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** filter */
    filter: z.string().nullish(),
  });
}

export function slidesSchema() {
  return z.object({
    /** الصورة */
    image: z.string(),
    /** blurHash */
    blurHash: z.string(),
    /** الرابط */
    link: z.string().nullish(),
  });
}

export function sectionSchema() {
  return z.object({
    /** العنوان */
    title: z.string().nullish(),
    /** عرض الكل */
    viewAllFilter: z.string().nullish(),
    /** التصميم */
    layout: z.enum(SectionLayoutList),
    /** العنوان الفرعي */
    subtitle: z.string().nullish(),
    /** الرابط */
    link: z.string().nullish(),
    /** المنتجات */
    products: z.array(productSchema()).nullish(),
    /** المتاجر */
    vendors: z.array(vendorSchema()).nullish(),
    /** الشرائح */
    slides: z.array(slidesSchema()).nullish(),
  });
}

export function landingPageResponseSchema() {
  return z.object({
    /** الأقسام */
    sections: z.array(sectionSchema()),
  });
}

export function addAddressBodySchema() {
  return z.object({
    /** التصنيف */
    label: z.string(),
    /** الشارع */
    street: z.string(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number(),
    /** الطول */
    longitude: z.number(),
  });
}

export function sendChatMessagePathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
  });
}

export function sendChatMessageBodySchema() {
  return z.object({
    /** النص */
    text: z.string(),
    /** المرفق */
    attachment: z.string().nullish(),
  });
}

export function markChatMessageAsSeenPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** معرف الرسالة */
    messageId: z.string(),
  });
}

export function updateChatMessagePathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** معرف الرسالة */
    messageId: z.string(),
  });
}

export function updateChatMessageBodySchema() {
  return z.object({
    /** النص */
    text: z.string().nullish(),
    /** المرفق */
    attachment: z.string().nullish(),
  });
}

export function updateAddressPathParamsSchema() {
  return z.object({
    /** معرف العنوان */
    addressId: z.string(),
  });
}

export function updateAddressBodySchema() {
  return z.object({
    /** التصنيف */
    label: z.string().nullish(),
    /** الشارع */
    street: z.string().nullish(),
    /** العنوان الداخلي */
    internalAddress: z.string().nullish(),
    /** الهاتف */
    phone: z.string().nullish(),
    /** رقم التليفون الثاني */
    phone2: z.string().nullish(),
    /** العرض */
    latitude: z.number().nullish(),
    /** الطول */
    longitude: z.number().nullish(),
  });
}

export function deleteAddressPathParamsSchema() {
  return z.object({
    /** معرف العنوان */
    addressId: z.string(),
  });
}

export function getPublicOrderPathParamsSchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
  });
}

export function placeOrderProductSchema() {
  return z.object({
    /** المنتج */
    productId: z.string(),
    /** الخصائص */
    variationId: z.string().nullish(),
    /** الكمية */
    quantity: z.number(),
    /** السعر النهائي */
    finalPrice: z.number(),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
  });
}

export function placeOrderBodySchema() {
  return z.object({
    /** المنتجات */
    orderProducts: z.array(placeOrderProductSchema()),
    /** العنوان */
    address: addressSchema().nullish(),
    /** طريقة الدفع */
    paymentMethod: z.enum(PaymentMethodList),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
    /** الملاحظات */
    notes: z.string().nullish(),
  });
}

export function buyNowBodySchema() {
  return z.object({
    /** المنتج */
    productId: z.string(),
    /** الخصائص */
    variationId: z.string(),
    /** السعر النهائي */
    finalPrice: z.number(),
    /** طريقة الدفع */
    paymentMethod: z.enum(PaymentMethodList),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
  });
}

export function getUserWalletQueryParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string().nullish(),
  });
}

export function walletReleasePathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
    /** معرف الحفظ */
    holdId: z.string(),
  });
}

export function walletReleaseResponseSchema() {
  return z.object({
    /** النجاح */
    success: z.boolean(),
    /** هل تم الحفظ بالفعل */
    alreadyReleased: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletMultiReleasePathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
  });
}

export function walletMultiReleaseResponseSchema() {
  return z.object({
    /** النجاح */
    success: z.boolean(),
    /** هل تم الحفظ بالفعل */
    alreadyReleased: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletMultiReleaseBodySchema() {
  return z.object({
    /** معرفات الحفظ */
    multiHoldIds: z.array(z.string()),
  });
}

export function walletCapturePathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
    /** معرف الحفظ */
    holdId: z.string(),
  });
}

export function walletCaptureResponseSchema() {
  return z.object({
    /** النجاح */
    success: z.boolean(),
    /** هل تم الحفظ بالفعل */
    alreadyCaptured: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletMultiCapturePathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
  });
}

export function walletMultiCaptureResponseSchema() {
  return z.object({
    /** النجاح */
    success: z.boolean(),
    /** هل تم الحفظ بالفعل */
    alreadyCaptured: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletMultiCaptureBodySchema() {
  return z.object({
    /** معرفات الحفظ */
    multiHoldIds: z.array(z.string()),
  });
}

export function walletRefundPathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
    /** معرف الترخيص */
    captureId: z.string(),
  });
}

export function walletRefundResponseSchema() {
  return z.object({
    /** النجاح */
    success: z.boolean(),
    /** هل تم الترجيع بالفعل */
    alreadyRefunded: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletHoldResponseSchema() {
  return z.object({
    /** معرف الحفظ */
    holdId: z.string(),
    /** النجاح */
    success: z.boolean(),
    /** هل تم الحفظ بالفعل */
    alreadyHold: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletHoldPathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
  });
}

export function walletHoldBodySchema() {
  return z.object({
    /** المبلغ */
    amount: z.number(),
    /** معرف الشحنة */
    idempotencyKey: z.string(),
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string().nullish(),
    /** معرف المنظمة */
    orgId: z.string().nullish(),
  });
}

export function walletMultiHoldResponseSchema() {
  return z.object({
    /** معرفات الحفظ */
    multiHoldIds: z.array(z.string()),
    /** النجاح */
    success: z.boolean(),
    /** هل تم الحفظ بالفعل */
    alreadyHold: z.boolean(),
    /** السبب */
    failReason: languageStringSchema().nullish(),
  });
}

export function walletMultiHoldPathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
  });
}

export function walletMultiHoldBodySchema() {
  return z.object({
    /** البيانات */
    multiHoldData: z.array(walletHoldBodySchema()),
  });
}

export function walletTopUpPathParamsSchema() {
  return z.object({
    /** معرف المستخدم */
    userId: z.string(),
  });
}

export function walletTopUpBodySchema() {
  return z.object({
    /** المبلغ */
    amount: decimalSchema,
    /** المبلغ بالدولار الأمريكي */
    usdAmount: decimalSchema.nullish(),
    /** معرف التكرار */
    idempotencyKey: z.string(),
    /** نسبة التبادل */
    exchangeRate: decimalSchema.nullish(),
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList),
    /** المزود */
    provider: z.enum(PaymentProviderList),
    /** معرف نية المزود */
    providerIntentId: z.string(),
  });
}

export function paymentIntentConfirmMetadataSchema() {
  return z.object({
    /** معلومات Moamalat */
    moamalat: z.any().nullish(),
    /** معلومات Masarat */
    masarat: z.any().nullish(),
  });
}

export function paymentIntentConfirmBodySchema() {
  return z.object({
    /** معرف نية الدفع */
    paymentIntentId: z.string(),
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList),
    /** البيانات */
    metadata: paymentIntentConfirmMetadataSchema(),
  });
}

export function createPaymentIntentBodySchema() {
  return z.object({
    /** نوع الشحن */
    type: z.enum(PaymentIntentTypeList),
    /** المبلغ */
    amount: decimalSchema,
    /** طريقة الشحن */
    topUpPaymentMethod: z.enum(TopUpPaymentMethodList),
    /** معرف الطلب */
    orderId: z.string().nullish(),
    /** البطاقة الشخصية */
    identityCard: z.string().nullish(),
    /** جلسة Masarat */
    masaratSessionToken: z.string().nullish(),
  });
}

export function paymentIntentResponseSchema() {
  return z.object({
    /** رابط الدفع */
    checkoutUrl: z.string().nullish(),
    /** البيانات */
    moamalatMetadata: z.any().nullish(),
    /** معرف نية الدفع */
    paymentIntentId: z.string(),
  });
}

export function walletTransactionsListResponseSchema() {
  return z.object({
    /** المعاملات */
    walletTransactions: z.array(walletTransactionSchema()),
    /** الترقيم */
    pagination: paginationSchema(),
  });
}

export function walletTransactionsListQueryParamsSchema() {
  return z.object({
    /** العدد الكلي لكل صفحة */
    totalPerPage: z.number(),
    /** الصفحة */
    page: z.number(),
    /** الترتيب */
    sort: z.string().nullish(),
    /** معرف المستخدم */
    userId: z.string().nullish(),
  });
}

export function exchangeRateResponseSchema() {
  return z.object({
    /** الدولار الأمريكي */
    usd: decimalSchema,
  });
}

export function addFcmTokenBodySchema() {
  return z.object({
    /** رمز FCM */
    token: z.string(),
  });
}

export function removeFcmTokenBodySchema() {
  return z.object({
    /** رمز FCM */
    token: z.string(),
  });
}

export function categoryCreateBodySchema() {
  return z.object({
    /** الاسم */
    name: z.string(),
  });
}

export function vendorCreateBodySchema() {
  return z.object({
    /** تم الحذف */
    deleted: z.boolean(),
    /** الاسم */
    name: z.string(),
    /** العنوان - التصنيف */
    addressLabel: z.string().nullish(),
    /** العنوان - الشارع */
    addressStreet: z.string().nullish(),
    /** العنوان - الداخلي */
    addressInternal: z.string().nullish(),
    /** العنوان - الهاتف */
    addressPhone: z.string().nullish(),
    /** العنوان - الهاتف الثاني */
    addressPhone2: z.string().nullish(),
    /** العنوان - العرض */
    addressLat: z.number().nullish(),
    /** العنوان - الطول */
    addressLng: z.number().nullish(),
    /** الشعار */
    logo: z.string().nullish(),
    /** الخلفية */
    background: z.string().nullish(),
  });
}

export function vendorDeletePathParamsSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string(),
  });
}

export function vendorUpdatePathParamsSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string(),
  });
}

export function vendorUpdateBodySchema() {
  return z.object({
    /** البيانات */
    vendor: vendorUpdateSchema(),
  });
}

export function productDeletePathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function productUpdatePathParamsSchema() {
  return z.object({
    /** معرف المنتج */
    productId: z.string(),
  });
}

export function productUpdateBodySchema() {
  return z.object({
    /** البيانات */
    product: productUpdateSchema(),
  });
}

export function productCreatePathParamsSchema() {
  return z.object({
    /** معرف المتجر */
    vendorId: z.string(),
  });
}

export function productCreateBodySchema() {
  return z.object({
    /** الحجم */
    size: z.enum(ProductSizeList),
    /** الاسم */
    name: z.string(),
    /** الوصف */
    description: z.string().nullish(),
    /** الصور */
    images: z.array(z.string()).nullish(),
    /** السعر */
    price: z.number().nullish(),
    /** السعر */
    discount: z.number().nullish(),
    /** الكمية */
    stock: z.number().nullish(),
    /** الخصائص */
    variations: z.array(productVariationSchema()).nullish(),
    /** الخلفية */
    bgColor: z.string().nullish(),
  });
}

export function productReviewCreateBodySchema() {
  return z.object({
    /** معرف الطلب */
    orderId: z.string(),
    /** معرف الطلب الفرعي */
    subOrderId: z.string(),
    /** المنتج */
    productId: z.string(),
    /** التقييم */
    rating: z.number(),
    /** المراجعة */
    review: z.string().nullish(),
  });
}

export namespace Entity {
  export type PaginationZod = ReturnType<typeof paginationSchema>;
  export type Pagination = z.infer<PaginationZod>;

  export type PaginationPaginatedResult = PaginatedResult<'paginations', Pagination>;

  export type DiffOperationZod = ReturnType<typeof diffOperationSchema>;
  export type DiffOperation = z.infer<DiffOperationZod>;

  export type DiffOperationPaginatedResult = PaginatedResult<'diffOperations', DiffOperation>;

  export type DiffOperationCreateZod = ReturnType<typeof diffOperationCreateSchema>;
  export type DiffOperationCreate = z.infer<DiffOperationCreateZod>;

  export type DiffOperationCreatePaginatedResult = PaginatedResult<'diffOperationCreates', DiffOperationCreate>;

  export type DiffOperationUpdateZod = ReturnType<typeof diffOperationUpdateSchema>;
  export type DiffOperationUpdate = z.infer<DiffOperationUpdateZod>;

  export type DiffOperationUpdatePaginatedResult = PaginatedResult<'diffOperationUpdates', DiffOperationUpdate>;

  export type LanguageStringZod = ReturnType<typeof languageStringSchema>;
  export type LanguageString = z.infer<LanguageStringZod>;

  export type LanguageStringPaginatedResult = PaginatedResult<'languageStrings', LanguageString>;

  export type LanguageStringCreateZod = ReturnType<typeof languageStringCreateSchema>;
  export type LanguageStringCreate = z.infer<LanguageStringCreateZod>;

  export type LanguageStringCreatePaginatedResult = PaginatedResult<'languageStringCreates', LanguageStringCreate>;

  export type LanguageStringUpdateZod = ReturnType<typeof languageStringUpdateSchema>;
  export type LanguageStringUpdate = z.infer<LanguageStringUpdateZod>;

  export type LanguageStringUpdatePaginatedResult = PaginatedResult<'languageStringUpdates', LanguageStringUpdate>;

  export type GeoLocationZod = ReturnType<typeof geoLocationSchema>;
  export type GeoLocation = z.infer<GeoLocationZod>;

  export type GeoLocationPaginatedResult = PaginatedResult<'geoLocations', GeoLocation>;

  export type GeoLocationCreateZod = ReturnType<typeof geoLocationCreateSchema>;
  export type GeoLocationCreate = z.infer<GeoLocationCreateZod>;

  export type GeoLocationCreatePaginatedResult = PaginatedResult<'geoLocationCreates', GeoLocationCreate>;

  export type GeoLocationUpdateZod = ReturnType<typeof geoLocationUpdateSchema>;
  export type GeoLocationUpdate = z.infer<GeoLocationUpdateZod>;

  export type GeoLocationUpdatePaginatedResult = PaginatedResult<'geoLocationUpdates', GeoLocationUpdate>;

  export type ShippingExternalLocationZod = ReturnType<typeof shippingExternalLocationSchema>;
  export type ShippingExternalLocation = z.infer<ShippingExternalLocationZod>;

  export type ShippingExternalLocationPaginatedResult = PaginatedResult<
    'shippingExternalLocations',
    ShippingExternalLocation
  >;

  export type ShippingExternalLocationCreateZod = ReturnType<typeof shippingExternalLocationCreateSchema>;
  export type ShippingExternalLocationCreate = z.infer<ShippingExternalLocationCreateZod>;

  export type ShippingExternalLocationCreatePaginatedResult = PaginatedResult<
    'shippingExternalLocationCreates',
    ShippingExternalLocationCreate
  >;

  export type ShippingExternalLocationUpdateZod = ReturnType<typeof shippingExternalLocationUpdateSchema>;
  export type ShippingExternalLocationUpdate = z.infer<ShippingExternalLocationUpdateZod>;

  export type ShippingExternalLocationUpdatePaginatedResult = PaginatedResult<
    'shippingExternalLocationUpdates',
    ShippingExternalLocationUpdate
  >;

  export type VendorZod = ReturnType<typeof vendorSchema>;
  export type Vendor = z.infer<VendorZod>;

  export type VendorPaginatedResult = PaginatedResult<'vendors', Vendor>;

  export type VendorCreateZod = ReturnType<typeof vendorCreateSchema>;
  export type VendorCreate = z.infer<VendorCreateZod>;

  export type VendorCreatePaginatedResult = PaginatedResult<'vendorCreates', VendorCreate>;

  export type VendorUpdateZod = ReturnType<typeof vendorUpdateSchema>;
  export type VendorUpdate = z.infer<VendorUpdateZod>;

  export type VendorUpdatePaginatedResult = PaginatedResult<'vendorUpdates', VendorUpdate>;

  export type ChatMessageZod = ReturnType<typeof chatMessageSchema>;
  export type ChatMessage = z.infer<ChatMessageZod>;

  export type ChatMessagePaginatedResult = PaginatedResult<'chatMessages', ChatMessage>;

  export type ChatMessageCreateZod = ReturnType<typeof chatMessageCreateSchema>;
  export type ChatMessageCreate = z.infer<ChatMessageCreateZod>;

  export type ChatMessageCreatePaginatedResult = PaginatedResult<'chatMessageCreates', ChatMessageCreate>;

  export type ChatMessageUpdateZod = ReturnType<typeof chatMessageUpdateSchema>;
  export type ChatMessageUpdate = z.infer<ChatMessageUpdateZod>;

  export type ChatMessageUpdatePaginatedResult = PaginatedResult<'chatMessageUpdates', ChatMessageUpdate>;

  export type ChatMessageReadZod = ReturnType<typeof chatMessageReadSchema>;
  export type ChatMessageRead = z.infer<ChatMessageReadZod>;

  export type ChatMessageReadPaginatedResult = PaginatedResult<'chatMessageReads', ChatMessageRead>;

  export type ChatMessageReadCreateZod = ReturnType<typeof chatMessageReadCreateSchema>;
  export type ChatMessageReadCreate = z.infer<ChatMessageReadCreateZod>;

  export type ChatMessageReadCreatePaginatedResult = PaginatedResult<'chatMessageReadCreates', ChatMessageReadCreate>;

  export type ChatMessageReadUpdateZod = ReturnType<typeof chatMessageReadUpdateSchema>;
  export type ChatMessageReadUpdate = z.infer<ChatMessageReadUpdateZod>;

  export type ChatMessageReadUpdatePaginatedResult = PaginatedResult<'chatMessageReadUpdates', ChatMessageReadUpdate>;

  export type VendorModerationZod = ReturnType<typeof vendorModerationSchema>;
  export type VendorModeration = z.infer<VendorModerationZod>;

  export type VendorModerationPaginatedResult = PaginatedResult<'vendorModerations', VendorModeration>;

  export type VendorModerationCreateZod = ReturnType<typeof vendorModerationCreateSchema>;
  export type VendorModerationCreate = z.infer<VendorModerationCreateZod>;

  export type VendorModerationCreatePaginatedResult = PaginatedResult<
    'vendorModerationCreates',
    VendorModerationCreate
  >;

  export type VendorModerationUpdateZod = ReturnType<typeof vendorModerationUpdateSchema>;
  export type VendorModerationUpdate = z.infer<VendorModerationUpdateZod>;

  export type VendorModerationUpdatePaginatedResult = PaginatedResult<
    'vendorModerationUpdates',
    VendorModerationUpdate
  >;

  export type ProductModerationZod = ReturnType<typeof productModerationSchema>;
  export type ProductModeration = z.infer<ProductModerationZod>;

  export type ProductModerationPaginatedResult = PaginatedResult<'productModerations', ProductModeration>;

  export type ProductModerationCreateZod = ReturnType<typeof productModerationCreateSchema>;
  export type ProductModerationCreate = z.infer<ProductModerationCreateZod>;

  export type ProductModerationCreatePaginatedResult = PaginatedResult<
    'productModerationCreates',
    ProductModerationCreate
  >;

  export type ProductModerationUpdateZod = ReturnType<typeof productModerationUpdateSchema>;
  export type ProductModerationUpdate = z.infer<ProductModerationUpdateZod>;

  export type ProductModerationUpdatePaginatedResult = PaginatedResult<
    'productModerationUpdates',
    ProductModerationUpdate
  >;

  export type SubOrderStatusEventZod = ReturnType<typeof subOrderStatusEventSchema>;
  export type SubOrderStatusEvent = z.infer<SubOrderStatusEventZod>;

  export type SubOrderStatusEventPaginatedResult = PaginatedResult<'subOrderStatusEvents', SubOrderStatusEvent>;

  export type SubOrderStatusEventCreateZod = ReturnType<typeof subOrderStatusEventCreateSchema>;
  export type SubOrderStatusEventCreate = z.infer<SubOrderStatusEventCreateZod>;

  export type SubOrderStatusEventCreatePaginatedResult = PaginatedResult<
    'subOrderStatusEventCreates',
    SubOrderStatusEventCreate
  >;

  export type SubOrderStatusEventUpdateZod = ReturnType<typeof subOrderStatusEventUpdateSchema>;
  export type SubOrderStatusEventUpdate = z.infer<SubOrderStatusEventUpdateZod>;

  export type SubOrderStatusEventUpdatePaginatedResult = PaginatedResult<
    'subOrderStatusEventUpdates',
    SubOrderStatusEventUpdate
  >;

  export type OrderZod = ReturnType<typeof orderSchema>;
  export type Order = z.infer<OrderZod>;

  export type OrderPaginatedResult = PaginatedResult<'orders', Order>;

  export type OrderCreateZod = ReturnType<typeof orderCreateSchema>;
  export type OrderCreate = z.infer<OrderCreateZod>;

  export type OrderCreatePaginatedResult = PaginatedResult<'orderCreates', OrderCreate>;

  export type SubOrderZod = ReturnType<typeof subOrderSchema>;
  export type SubOrder = z.infer<SubOrderZod>;

  export type SubOrderPaginatedResult = PaginatedResult<'subOrders', SubOrder>;

  export type SubOrderCreateZod = ReturnType<typeof subOrderCreateSchema>;
  export type SubOrderCreate = z.infer<SubOrderCreateZod>;

  export type SubOrderCreatePaginatedResult = PaginatedResult<'subOrderCreates', SubOrderCreate>;

  export type SubOrderUpdateZod = ReturnType<typeof subOrderUpdateSchema>;
  export type SubOrderUpdate = z.infer<SubOrderUpdateZod>;

  export type SubOrderUpdatePaginatedResult = PaginatedResult<'subOrderUpdates', SubOrderUpdate>;

  export type PaymentIntentMetaDataZod = ReturnType<typeof paymentIntentMetaDataSchema>;
  export type PaymentIntentMetaData = z.infer<PaymentIntentMetaDataZod>;

  export type PaymentIntentMetaDataPaginatedResult = PaginatedResult<'paymentIntentMetaData', PaymentIntentMetaData>;

  export type PaymentIntentMetaDataCreateZod = ReturnType<typeof paymentIntentMetaDataCreateSchema>;
  export type PaymentIntentMetaDataCreate = z.infer<PaymentIntentMetaDataCreateZod>;

  export type PaymentIntentMetaDataCreatePaginatedResult = PaginatedResult<
    'paymentIntentMetaDataCreates',
    PaymentIntentMetaDataCreate
  >;

  export type PaymentIntentMetaDataUpdateZod = ReturnType<typeof paymentIntentMetaDataUpdateSchema>;
  export type PaymentIntentMetaDataUpdate = z.infer<PaymentIntentMetaDataUpdateZod>;

  export type PaymentIntentMetaDataUpdatePaginatedResult = PaginatedResult<
    'paymentIntentMetaDataUpdates',
    PaymentIntentMetaDataUpdate
  >;

  export type PaymentIntentWebhookZod = ReturnType<typeof paymentIntentWebhookSchema>;
  export type PaymentIntentWebhook = z.infer<PaymentIntentWebhookZod>;

  export type PaymentIntentWebhookPaginatedResult = PaginatedResult<'paymentIntentWebhooks', PaymentIntentWebhook>;

  export type ProductVariationMetadataZod = ReturnType<typeof productVariationMetadataSchema>;
  export type ProductVariationMetadata = z.infer<ProductVariationMetadataZod>;

  export type ProductVariationMetadataPaginatedResult = PaginatedResult<
    'productVariationMetadata',
    ProductVariationMetadata
  >;

  export type ProductVariationMetadataCreateZod = ReturnType<typeof productVariationMetadataCreateSchema>;
  export type ProductVariationMetadataCreate = z.infer<ProductVariationMetadataCreateZod>;

  export type ProductVariationMetadataCreatePaginatedResult = PaginatedResult<
    'productVariationMetadataCreates',
    ProductVariationMetadataCreate
  >;

  export type ProductVariationMetadataUpdateZod = ReturnType<typeof productVariationMetadataUpdateSchema>;
  export type ProductVariationMetadataUpdate = z.infer<ProductVariationMetadataUpdateZod>;

  export type ProductVariationMetadataUpdatePaginatedResult = PaginatedResult<
    'productVariationMetadataUpdates',
    ProductVariationMetadataUpdate
  >;

  export type ProductVariationZod = ReturnType<typeof productVariationSchema>;
  export type ProductVariation = z.infer<ProductVariationZod>;

  export type ProductVariationPaginatedResult = PaginatedResult<'productVariations', ProductVariation>;

  export type ProductVariationCreateZod = ReturnType<typeof productVariationCreateSchema>;
  export type ProductVariationCreate = z.infer<ProductVariationCreateZod>;

  export type ProductVariationCreatePaginatedResult = PaginatedResult<
    'productVariationCreates',
    ProductVariationCreate
  >;

  export type ProductVariationUpdateZod = ReturnType<typeof productVariationUpdateSchema>;
  export type ProductVariationUpdate = z.infer<ProductVariationUpdateZod>;

  export type ProductVariationUpdatePaginatedResult = PaginatedResult<
    'productVariationUpdates',
    ProductVariationUpdate
  >;

  export type OrderProductZod = ReturnType<typeof orderProductSchema>;
  export type OrderProduct = z.infer<OrderProductZod>;

  export type OrderProductPaginatedResult = PaginatedResult<'orderProducts', OrderProduct>;

  export type OrderProductCreateZod = ReturnType<typeof orderProductCreateSchema>;
  export type OrderProductCreate = z.infer<OrderProductCreateZod>;

  export type OrderProductCreatePaginatedResult = PaginatedResult<'orderProductCreates', OrderProductCreate>;

  export type OrderProductMetaDataZod = ReturnType<typeof orderProductMetaDataSchema>;
  export type OrderProductMetaData = z.infer<OrderProductMetaDataZod>;

  export type OrderProductMetaDataPaginatedResult = PaginatedResult<'orderProductMetaData', OrderProductMetaData>;

  export type OrderProductMetaDataCreateZod = ReturnType<typeof orderProductMetaDataCreateSchema>;
  export type OrderProductMetaDataCreate = z.infer<OrderProductMetaDataCreateZod>;

  export type OrderProductMetaDataCreatePaginatedResult = PaginatedResult<
    'orderProductMetaDataCreates',
    OrderProductMetaDataCreate
  >;

  export type OrderProductMetaDataUpdateZod = ReturnType<typeof orderProductMetaDataUpdateSchema>;
  export type OrderProductMetaDataUpdate = z.infer<OrderProductMetaDataUpdateZod>;

  export type OrderProductMetaDataUpdatePaginatedResult = PaginatedResult<
    'orderProductMetaDataUpdates',
    OrderProductMetaDataUpdate
  >;

  export type CategoryZod = ReturnType<typeof categorySchema>;
  export type Category = z.infer<CategoryZod>;

  export type CategoryPaginatedResult = PaginatedResult<'categories', Category>;

  export type CategoryCreateZod = ReturnType<typeof categoryCreateSchema>;
  export type CategoryCreate = z.infer<CategoryCreateZod>;

  export type CategoryCreatePaginatedResult = PaginatedResult<'categoryCreates', CategoryCreate>;

  export type CategoryUpdateZod = ReturnType<typeof categoryUpdateSchema>;
  export type CategoryUpdate = z.infer<CategoryUpdateZod>;

  export type CategoryUpdatePaginatedResult = PaginatedResult<'categoryUpdates', CategoryUpdate>;

  export type NumberRangeZod = ReturnType<typeof numberRangeSchema>;
  export type NumberRange = z.infer<NumberRangeZod>;

  export type NumberRangePaginatedResult = PaginatedResult<'numberRanges', NumberRange>;

  export type NumberRangeCreateZod = ReturnType<typeof numberRangeCreateSchema>;
  export type NumberRangeCreate = z.infer<NumberRangeCreateZod>;

  export type NumberRangeCreatePaginatedResult = PaginatedResult<'numberRangeCreates', NumberRangeCreate>;

  export type NumberRangeUpdateZod = ReturnType<typeof numberRangeUpdateSchema>;
  export type NumberRangeUpdate = z.infer<NumberRangeUpdateZod>;

  export type NumberRangeUpdatePaginatedResult = PaginatedResult<'numberRangeUpdates', NumberRangeUpdate>;

  export type VoucherZod = ReturnType<typeof voucherSchema>;
  export type Voucher = z.infer<VoucherZod>;

  export type VoucherPaginatedResult = PaginatedResult<'vouchers', Voucher>;

  export type ProductZod = ReturnType<typeof productSchema>;
  export type Product = z.infer<ProductZod>;

  export type ProductPaginatedResult = PaginatedResult<'products', Product>;

  export type ProductCreateZod = ReturnType<typeof productCreateSchema>;
  export type ProductCreate = z.infer<ProductCreateZod>;

  export type ProductCreatePaginatedResult = PaginatedResult<'productCreates', ProductCreate>;

  export type ProductUpdateZod = ReturnType<typeof productUpdateSchema>;
  export type ProductUpdate = z.infer<ProductUpdateZod>;

  export type ProductUpdatePaginatedResult = PaginatedResult<'productUpdates', ProductUpdate>;

  export type VariationAttributeZod = ReturnType<typeof variationAttributeSchema>;
  export type VariationAttribute = z.infer<VariationAttributeZod>;

  export type VariationAttributePaginatedResult = PaginatedResult<'variationAttributes', VariationAttribute>;

  export type VariationAttributeCreateZod = ReturnType<typeof variationAttributeCreateSchema>;
  export type VariationAttributeCreate = z.infer<VariationAttributeCreateZod>;

  export type VariationAttributeCreatePaginatedResult = PaginatedResult<
    'variationAttributeCreates',
    VariationAttributeCreate
  >;

  export type VariationAttributeUpdateZod = ReturnType<typeof variationAttributeUpdateSchema>;
  export type VariationAttributeUpdate = z.infer<VariationAttributeUpdateZod>;

  export type VariationAttributeUpdatePaginatedResult = PaginatedResult<
    'variationAttributeUpdates',
    VariationAttributeUpdate
  >;

  export type AttachmentZod = ReturnType<typeof attachmentSchema>;
  export type Attachment = z.infer<AttachmentZod>;

  export type AttachmentPaginatedResult = PaginatedResult<'attachments', Attachment>;

  export type AttachmentCreateZod = ReturnType<typeof attachmentCreateSchema>;
  export type AttachmentCreate = z.infer<AttachmentCreateZod>;

  export type AttachmentCreatePaginatedResult = PaginatedResult<'attachmentCreates', AttachmentCreate>;

  export type AttachmentUpdateZod = ReturnType<typeof attachmentUpdateSchema>;
  export type AttachmentUpdate = z.infer<AttachmentUpdateZod>;

  export type AttachmentUpdatePaginatedResult = PaginatedResult<'attachmentUpdates', AttachmentUpdate>;

  export type ProductReviewZod = ReturnType<typeof productReviewSchema>;
  export type ProductReview = z.infer<ProductReviewZod>;

  export type ProductReviewPaginatedResult = PaginatedResult<'productReviews', ProductReview>;

  export type ProductReviewCreateZod = ReturnType<typeof productReviewCreateSchema>;
  export type ProductReviewCreate = z.infer<ProductReviewCreateZod>;

  export type ProductReviewCreatePaginatedResult = PaginatedResult<'productReviewCreates', ProductReviewCreate>;

  export type ProductReviewUpdateZod = ReturnType<typeof productReviewUpdateSchema>;
  export type ProductReviewUpdate = z.infer<ProductReviewUpdateZod>;

  export type ProductReviewUpdatePaginatedResult = PaginatedResult<'productReviewUpdates', ProductReviewUpdate>;

  export type AddressZod = ReturnType<typeof addressSchema>;
  export type Address = z.infer<AddressZod>;

  export type AddressPaginatedResult = PaginatedResult<'addresses', Address>;

  export type AddressCreateZod = ReturnType<typeof addressCreateSchema>;
  export type AddressCreate = z.infer<AddressCreateZod>;

  export type AddressCreatePaginatedResult = PaginatedResult<'addressCreates', AddressCreate>;

  export type AddressUpdateZod = ReturnType<typeof addressUpdateSchema>;
  export type AddressUpdate = z.infer<AddressUpdateZod>;

  export type AddressUpdatePaginatedResult = PaginatedResult<'addressUpdates', AddressUpdate>;

  export type UserAddressZod = ReturnType<typeof userAddressSchema>;
  export type UserAddress = z.infer<UserAddressZod>;

  export type UserAddressPaginatedResult = PaginatedResult<'userAddresses', UserAddress>;

  export type UserAddressCreateZod = ReturnType<typeof userAddressCreateSchema>;
  export type UserAddressCreate = z.infer<UserAddressCreateZod>;

  export type UserAddressCreatePaginatedResult = PaginatedResult<'userAddressCreates', UserAddressCreate>;

  export type UserAddressUpdateZod = ReturnType<typeof userAddressUpdateSchema>;
  export type UserAddressUpdate = z.infer<UserAddressUpdateZod>;

  export type UserAddressUpdatePaginatedResult = PaginatedResult<'userAddressUpdates', UserAddressUpdate>;

  export type UserZod = ReturnType<typeof userSchema>;
  export type User = z.infer<UserZod>;

  export type UserPaginatedResult = PaginatedResult<'users', User>;

  export type UserUpdateZod = ReturnType<typeof userUpdateSchema>;
  export type UserUpdate = z.infer<UserUpdateZod>;

  export type UserUpdatePaginatedResult = PaginatedResult<'userUpdates', UserUpdate>;

  export type WalletTransactionZod = ReturnType<typeof walletTransactionSchema>;
  export type WalletTransaction = z.infer<WalletTransactionZod>;

  export type WalletTransactionPaginatedResult = PaginatedResult<'walletTransactions', WalletTransaction>;

  export type WalletTransactionUpdateZod = ReturnType<typeof walletTransactionUpdateSchema>;
  export type WalletTransactionUpdate = z.infer<WalletTransactionUpdateZod>;

  export type WalletTransactionUpdatePaginatedResult = PaginatedResult<
    'walletTransactionUpdates',
    WalletTransactionUpdate
  >;

  export type WalletZod = ReturnType<typeof walletSchema>;
  export type Wallet = z.infer<WalletZod>;

  export type WalletPaginatedResult = PaginatedResult<'wallets', Wallet>;

  export type WalletUpdateZod = ReturnType<typeof walletUpdateSchema>;
  export type WalletUpdate = z.infer<WalletUpdateZod>;

  export type WalletUpdatePaginatedResult = PaginatedResult<'walletUpdates', WalletUpdate>;

  export type PaymentIntentZod = ReturnType<typeof paymentIntentSchema>;
  export type PaymentIntent = z.infer<PaymentIntentZod>;

  export type PaymentIntentPaginatedResult = PaginatedResult<'paymentIntents', PaymentIntent>;

  export type PaymentIntentUpdateZod = ReturnType<typeof paymentIntentUpdateSchema>;
  export type PaymentIntentUpdate = z.infer<PaymentIntentUpdateZod>;

  export type PaymentIntentUpdatePaginatedResult = PaginatedResult<'paymentIntentUpdates', PaymentIntentUpdate>;

  export type SharedCartItemZod = ReturnType<typeof sharedCartItemSchema>;
  export type SharedCartItem = z.infer<SharedCartItemZod>;

  export type SharedCartItemPaginatedResult = PaginatedResult<'sharedCartItems', SharedCartItem>;

  export type SharedCartZod = ReturnType<typeof sharedCartSchema>;
  export type SharedCart = z.infer<SharedCartZod>;

  export type SharedCartPaginatedResult = PaginatedResult<'sharedCarts', SharedCart>;

  export type UserWishlistZod = ReturnType<typeof userWishlistSchema>;
  export type UserWishlist = z.infer<UserWishlistZod>;

  export type UserWishlistPaginatedResult = PaginatedResult<'userWishlists', UserWishlist>;

  export type WPayWebhookZod = ReturnType<typeof wPayWebhookSchema>;
  export type WPayWebhook = z.infer<WPayWebhookZod>;

  export type WPayWebhookPaginatedResult = PaginatedResult<'wPayWebhooks', WPayWebhook>;

  export type ShippingFeeZod = ReturnType<typeof shippingFeeSchema>;
  export type ShippingFee = z.infer<ShippingFeeZod>;

  export type ShippingFeePaginatedResult = PaginatedResult<'shippingFees', ShippingFee>;

  export type SharedCartBodyZod = ReturnType<typeof sharedCartBodySchema>;
  export type SharedCartBody = z.infer<SharedCartBodyZod>;

  export type SharedCartBodyPaginatedResult = PaginatedResult<'sharedCartBodies', SharedCartBody>;

  export type ShareCartResponseZod = ReturnType<typeof shareCartResponseSchema>;
  export type ShareCartResponse = z.infer<ShareCartResponseZod>;

  export type ShareCartResponsePaginatedResult = PaginatedResult<'shareCartResponses', ShareCartResponse>;

  export type GetSharedCartPathParamsZod = ReturnType<typeof getSharedCartPathParamsSchema>;
  export type GetSharedCartPathParams = z.infer<GetSharedCartPathParamsZod>;

  export type GetSharedCartPathParamsPaginatedResult = PaginatedResult<
    'getSharedCartPathParams',
    GetSharedCartPathParams
  >;

  export type GetSharedCartItemZod = ReturnType<typeof getSharedCartItemSchema>;
  export type GetSharedCartItem = z.infer<GetSharedCartItemZod>;

  export type GetSharedCartItemPaginatedResult = PaginatedResult<'getSharedCartItems', GetSharedCartItem>;

  export type GetSharedCartResponseZod = ReturnType<typeof getSharedCartResponseSchema>;
  export type GetSharedCartResponse = z.infer<GetSharedCartResponseZod>;

  export type GetSharedCartResponsePaginatedResult = PaginatedResult<'getSharedCartResponses', GetSharedCartResponse>;

  export type OrderProductForShippingResponseZod = ReturnType<typeof orderProductForShippingResponseSchema>;
  export type OrderProductForShippingResponse = z.infer<OrderProductForShippingResponseZod>;

  export type OrderProductForShippingResponsePaginatedResult = PaginatedResult<
    'orderProductForShippingResponses',
    OrderProductForShippingResponse
  >;

  export type OrderProductForShippingCostZod = ReturnType<typeof orderProductForShippingCostSchema>;
  export type OrderProductForShippingCost = z.infer<OrderProductForShippingCostZod>;

  export type OrderProductForShippingCostPaginatedResult = PaginatedResult<
    'orderProductForShippingCosts',
    OrderProductForShippingCost
  >;

  export type CalculateShippingCostBodyZod = ReturnType<typeof calculateShippingCostBodySchema>;
  export type CalculateShippingCostBody = z.infer<CalculateShippingCostBodyZod>;

  export type CalculateShippingCostBodyPaginatedResult = PaginatedResult<
    'calculateShippingCostBodies',
    CalculateShippingCostBody
  >;

  export type VendorGetProductByIdPathParamsZod = ReturnType<typeof vendorGetProductByIdPathParamsSchema>;
  export type VendorGetProductByIdPathParams = z.infer<VendorGetProductByIdPathParamsZod>;

  export type VendorGetProductByIdPathParamsPaginatedResult = PaginatedResult<
    'vendorGetProductByIdPathParams',
    VendorGetProductByIdPathParams
  >;

  export type GetProductByIdPathParamsZod = ReturnType<typeof getProductByIdPathParamsSchema>;
  export type GetProductByIdPathParams = z.infer<GetProductByIdPathParamsZod>;

  export type GetProductByIdPathParamsPaginatedResult = PaginatedResult<
    'getProductByIdPathParams',
    GetProductByIdPathParams
  >;

  export type GetSubOrderChatResponseZod = ReturnType<typeof getSubOrderChatResponseSchema>;
  export type GetSubOrderChatResponse = z.infer<GetSubOrderChatResponseZod>;

  export type GetSubOrderChatResponsePaginatedResult = PaginatedResult<
    'getSubOrderChatResponses',
    GetSubOrderChatResponse
  >;

  export type GetSubOrderChatPathParamsZod = ReturnType<typeof getSubOrderChatPathParamsSchema>;
  export type GetSubOrderChatPathParams = z.infer<GetSubOrderChatPathParamsZod>;

  export type GetSubOrderChatPathParamsPaginatedResult = PaginatedResult<
    'getSubOrderChatPathParams',
    GetSubOrderChatPathParams
  >;

  export type GetOrderByIdPathParamsZod = ReturnType<typeof getOrderByIdPathParamsSchema>;
  export type GetOrderByIdPathParams = z.infer<GetOrderByIdPathParamsZod>;

  export type GetOrderByIdPathParamsPaginatedResult = PaginatedResult<'getOrderByIdPathParams', GetOrderByIdPathParams>;

  export type ProductsListResponseZod = ReturnType<typeof productsListResponseSchema>;
  export type ProductsListResponse = z.infer<ProductsListResponseZod>;

  export type ProductsListResponsePaginatedResult = PaginatedResult<'productsListResponses', ProductsListResponse>;

  export type ProductsListQueryParamsZod = ReturnType<typeof productsListQueryParamsSchema>;
  export type ProductsListQueryParams = z.infer<ProductsListQueryParamsZod>;

  export type ProductsListQueryParamsPaginatedResult = PaginatedResult<
    'productsListQueryParams',
    ProductsListQueryParams
  >;

  export type RelatedProductsListPathParamsZod = ReturnType<typeof relatedProductsListPathParamsSchema>;
  export type RelatedProductsListPathParams = z.infer<RelatedProductsListPathParamsZod>;

  export type RelatedProductsListPathParamsPaginatedResult = PaginatedResult<
    'relatedProductsListPathParams',
    RelatedProductsListPathParams
  >;

  export type RelatedProductsListQueryParamsZod = ReturnType<typeof relatedProductsListQueryParamsSchema>;
  export type RelatedProductsListQueryParams = z.infer<RelatedProductsListQueryParamsZod>;

  export type RelatedProductsListQueryParamsPaginatedResult = PaginatedResult<
    'relatedProductsListQueryParams',
    RelatedProductsListQueryParams
  >;

  export type RelatedProductsListResponseZod = ReturnType<typeof relatedProductsListResponseSchema>;
  export type RelatedProductsListResponse = z.infer<RelatedProductsListResponseZod>;

  export type RelatedProductsListResponsePaginatedResult = PaginatedResult<
    'relatedProductsListResponses',
    RelatedProductsListResponse
  >;

  export type ProductReviewsListPathParamsZod = ReturnType<typeof productReviewsListPathParamsSchema>;
  export type ProductReviewsListPathParams = z.infer<ProductReviewsListPathParamsZod>;

  export type ProductReviewsListPathParamsPaginatedResult = PaginatedResult<
    'productReviewsListPathParams',
    ProductReviewsListPathParams
  >;

  export type ProductReviewsListQueryParamsZod = ReturnType<typeof productReviewsListQueryParamsSchema>;
  export type ProductReviewsListQueryParams = z.infer<ProductReviewsListQueryParamsZod>;

  export type ProductReviewsListQueryParamsPaginatedResult = PaginatedResult<
    'productReviewsListQueryParams',
    ProductReviewsListQueryParams
  >;

  export type ProductReviewsListResponseZod = ReturnType<typeof productReviewsListResponseSchema>;
  export type ProductReviewsListResponse = z.infer<ProductReviewsListResponseZod>;

  export type ProductReviewsListResponsePaginatedResult = PaginatedResult<
    'productReviewsListResponses',
    ProductReviewsListResponse
  >;

  export type RequestToPublishProductPathParamsZod = ReturnType<typeof requestToPublishProductPathParamsSchema>;
  export type RequestToPublishProductPathParams = z.infer<RequestToPublishProductPathParamsZod>;

  export type RequestToPublishProductPathParamsPaginatedResult = PaginatedResult<
    'requestToPublishProductPathParams',
    RequestToPublishProductPathParams
  >;

  export type UnPublishProductPathParamsZod = ReturnType<typeof unPublishProductPathParamsSchema>;
  export type UnPublishProductPathParams = z.infer<UnPublishProductPathParamsZod>;

  export type UnPublishProductPathParamsPaginatedResult = PaginatedResult<
    'unPublishProductPathParams',
    UnPublishProductPathParams
  >;

  export type RequestToPublishVendorPathParamsZod = ReturnType<typeof requestToPublishVendorPathParamsSchema>;
  export type RequestToPublishVendorPathParams = z.infer<RequestToPublishVendorPathParamsZod>;

  export type RequestToPublishVendorPathParamsPaginatedResult = PaginatedResult<
    'requestToPublishVendorPathParams',
    RequestToPublishVendorPathParams
  >;

  export type UnPublishVendorPathParamsZod = ReturnType<typeof unPublishVendorPathParamsSchema>;
  export type UnPublishVendorPathParams = z.infer<UnPublishVendorPathParamsZod>;

  export type UnPublishVendorPathParamsPaginatedResult = PaginatedResult<
    'unPublishVendorPathParams',
    UnPublishVendorPathParams
  >;

  export type GetVendorByIdQueryParamsZod = ReturnType<typeof getVendorByIdQueryParamsSchema>;
  export type GetVendorByIdQueryParams = z.infer<GetVendorByIdQueryParamsZod>;

  export type GetVendorByIdQueryParamsPaginatedResult = PaginatedResult<
    'getVendorByIdQueryParams',
    GetVendorByIdQueryParams
  >;

  export type GetVendorByIdPathParamsZod = ReturnType<typeof getVendorByIdPathParamsSchema>;
  export type GetVendorByIdPathParams = z.infer<GetVendorByIdPathParamsZod>;

  export type GetVendorByIdPathParamsPaginatedResult = PaginatedResult<
    'getVendorByIdPathParams',
    GetVendorByIdPathParams
  >;

  export type VendorsListResponseZod = ReturnType<typeof vendorsListResponseSchema>;
  export type VendorsListResponse = z.infer<VendorsListResponseZod>;

  export type VendorsListResponsePaginatedResult = PaginatedResult<'vendorsListResponses', VendorsListResponse>;

  export type VendorsListQueryParamsZod = ReturnType<typeof vendorsListQueryParamsSchema>;
  export type VendorsListQueryParams = z.infer<VendorsListQueryParamsZod>;

  export type VendorsListQueryParamsPaginatedResult = PaginatedResult<'vendorsListQueryParams', VendorsListQueryParams>;

  export type VendorsListForVendorsResponseZod = ReturnType<typeof vendorsListForVendorsResponseSchema>;
  export type VendorsListForVendorsResponse = z.infer<VendorsListForVendorsResponseZod>;

  export type VendorsListForVendorsResponsePaginatedResult = PaginatedResult<
    'vendorsListForVendorsResponses',
    VendorsListForVendorsResponse
  >;

  export type VendorsListForVendorsQueryParamsZod = ReturnType<typeof vendorsListForVendorsQueryParamsSchema>;
  export type VendorsListForVendorsQueryParams = z.infer<VendorsListForVendorsQueryParamsZod>;

  export type VendorsListForVendorsQueryParamsPaginatedResult = PaginatedResult<
    'vendorsListForVendorsQueryParams',
    VendorsListForVendorsQueryParams
  >;

  export type CategoriesListResponseZod = ReturnType<typeof categoriesListResponseSchema>;
  export type CategoriesListResponse = z.infer<CategoriesListResponseZod>;

  export type CategoriesListResponsePaginatedResult = PaginatedResult<
    'categoriesListResponses',
    CategoriesListResponse
  >;

  export type CategoriesListQueryParamsZod = ReturnType<typeof categoriesListQueryParamsSchema>;
  export type CategoriesListQueryParams = z.infer<CategoriesListQueryParamsZod>;

  export type CategoriesListQueryParamsPaginatedResult = PaginatedResult<
    'categoriesListQueryParams',
    CategoriesListQueryParams
  >;

  export type OrdersListResponseZod = ReturnType<typeof ordersListResponseSchema>;
  export type OrdersListResponse = z.infer<OrdersListResponseZod>;

  export type OrdersListResponsePaginatedResult = PaginatedResult<'ordersListResponses', OrdersListResponse>;

  export type OrdersListQueryParamsZod = ReturnType<typeof ordersListQueryParamsSchema>;
  export type OrdersListQueryParams = z.infer<OrdersListQueryParamsZod>;

  export type OrdersListQueryParamsPaginatedResult = PaginatedResult<'ordersListQueryParams', OrdersListQueryParams>;

  export type OrdersListCustomersResponseZod = ReturnType<typeof ordersListCustomersResponseSchema>;
  export type OrdersListCustomersResponse = z.infer<OrdersListCustomersResponseZod>;

  export type OrdersListCustomersResponsePaginatedResult = PaginatedResult<
    'ordersListCustomersResponses',
    OrdersListCustomersResponse
  >;

  export type OrdersListOrdersListCustomersQueryParamsZod = ReturnType<
    typeof ordersListOrdersListCustomersQueryParamsSchema
  >;
  export type OrdersListOrdersListCustomersQueryParams = z.infer<OrdersListOrdersListCustomersQueryParamsZod>;

  export type OrdersListOrdersListCustomersQueryParamsPaginatedResult = PaginatedResult<
    'ordersListOrdersListCustomersQueryParams',
    OrdersListOrdersListCustomersQueryParams
  >;

  export type AdminProductsListResponseZod = ReturnType<typeof adminProductsListResponseSchema>;
  export type AdminProductsListResponse = z.infer<AdminProductsListResponseZod>;

  export type AdminProductsListResponsePaginatedResult = PaginatedResult<
    'adminProductsListResponses',
    AdminProductsListResponse
  >;

  export type AdminProductsListQueryParamsZod = ReturnType<typeof adminProductsListQueryParamsSchema>;
  export type AdminProductsListQueryParams = z.infer<AdminProductsListQueryParamsZod>;

  export type AdminProductsListQueryParamsPaginatedResult = PaginatedResult<
    'adminProductsListQueryParams',
    AdminProductsListQueryParams
  >;

  export type AdminProductsDecisionItemZod = ReturnType<typeof adminProductsDecisionItemSchema>;
  export type AdminProductsDecisionItem = z.infer<AdminProductsDecisionItemZod>;

  export type AdminProductsDecisionItemPaginatedResult = PaginatedResult<
    'adminProductsDecisionItems',
    AdminProductsDecisionItem
  >;

  export type AdminProductsDecisionListZod = ReturnType<typeof adminProductsDecisionListSchema>;
  export type AdminProductsDecisionList = z.infer<AdminProductsDecisionListZod>;

  export type AdminProductsDecisionListPaginatedResult = PaginatedResult<
    'adminProductsDecisionLists',
    AdminProductsDecisionList
  >;

  export type AdminVendorsListResponseZod = ReturnType<typeof adminVendorsListResponseSchema>;
  export type AdminVendorsListResponse = z.infer<AdminVendorsListResponseZod>;

  export type AdminVendorsListResponsePaginatedResult = PaginatedResult<
    'adminVendorsListResponses',
    AdminVendorsListResponse
  >;

  export type AdminVendorsListQueryParamsZod = ReturnType<typeof adminVendorsListQueryParamsSchema>;
  export type AdminVendorsListQueryParams = z.infer<AdminVendorsListQueryParamsZod>;

  export type AdminVendorsListQueryParamsPaginatedResult = PaginatedResult<
    'adminVendorsListQueryParams',
    AdminVendorsListQueryParams
  >;

  export type AdminVendorsDecisionItemZod = ReturnType<typeof adminVendorsDecisionItemSchema>;
  export type AdminVendorsDecisionItem = z.infer<AdminVendorsDecisionItemZod>;

  export type AdminVendorsDecisionItemPaginatedResult = PaginatedResult<
    'adminVendorsDecisionItems',
    AdminVendorsDecisionItem
  >;

  export type AdminVendorsDecisionListZod = ReturnType<typeof adminVendorsDecisionListSchema>;
  export type AdminVendorsDecisionList = z.infer<AdminVendorsDecisionListZod>;

  export type AdminVendorsDecisionListPaginatedResult = PaginatedResult<
    'adminVendorsDecisionLists',
    AdminVendorsDecisionList
  >;

  export type MarkOrderAsReadyForShippingPathParamsZod = ReturnType<typeof markOrderAsReadyForShippingPathParamsSchema>;
  export type MarkOrderAsReadyForShippingPathParams = z.infer<MarkOrderAsReadyForShippingPathParamsZod>;

  export type MarkOrderAsReadyForShippingPathParamsPaginatedResult = PaginatedResult<
    'markOrderAsReadyForShippingPathParams',
    MarkOrderAsReadyForShippingPathParams
  >;

  export type MarkOrderAsProcessingPathParamsZod = ReturnType<typeof markOrderAsProcessingPathParamsSchema>;
  export type MarkOrderAsProcessingPathParams = z.infer<MarkOrderAsProcessingPathParamsZod>;

  export type MarkOrderAsProcessingPathParamsPaginatedResult = PaginatedResult<
    'markOrderAsProcessingPathParams',
    MarkOrderAsProcessingPathParams
  >;

  export type CancelOrderPathParamsZod = ReturnType<typeof cancelOrderPathParamsSchema>;
  export type CancelOrderPathParams = z.infer<CancelOrderPathParamsZod>;

  export type CancelOrderPathParamsPaginatedResult = PaginatedResult<'cancelOrderPathParams', CancelOrderPathParams>;

  export type CancelOrderBodyZod = ReturnType<typeof cancelOrderBodySchema>;
  export type CancelOrderBody = z.infer<CancelOrderBodyZod>;

  export type CancelOrderBodyPaginatedResult = PaginatedResult<'cancelOrderBodies', CancelOrderBody>;

  export type AddToWishlistPathParamsZod = ReturnType<typeof addToWishlistPathParamsSchema>;
  export type AddToWishlistPathParams = z.infer<AddToWishlistPathParamsZod>;

  export type AddToWishlistPathParamsPaginatedResult = PaginatedResult<
    'addToWishlistPathParams',
    AddToWishlistPathParams
  >;

  export type RemoveFromWishlistPathParamsZod = ReturnType<typeof removeFromWishlistPathParamsSchema>;
  export type RemoveFromWishlistPathParams = z.infer<RemoveFromWishlistPathParamsZod>;

  export type RemoveFromWishlistPathParamsPaginatedResult = PaginatedResult<
    'removeFromWishlistPathParams',
    RemoveFromWishlistPathParams
  >;

  export type WishListResponseZod = ReturnType<typeof wishListResponseSchema>;
  export type WishListResponse = z.infer<WishListResponseZod>;

  export type WishListResponsePaginatedResult = PaginatedResult<'wishListResponses', WishListResponse>;

  export type WishListQueryParamsZod = ReturnType<typeof wishListQueryParamsSchema>;
  export type WishListQueryParams = z.infer<WishListQueryParamsZod>;

  export type WishListQueryParamsPaginatedResult = PaginatedResult<'wishListQueryParams', WishListQueryParams>;

  export type ProductsListByFilterResponseZod = ReturnType<typeof productsListByFilterResponseSchema>;
  export type ProductsListByFilterResponse = z.infer<ProductsListByFilterResponseZod>;

  export type ProductsListByFilterResponsePaginatedResult = PaginatedResult<
    'productsListByFilterResponses',
    ProductsListByFilterResponse
  >;

  export type ProductsListByFilterQueryParamsZod = ReturnType<typeof productsListByFilterQueryParamsSchema>;
  export type ProductsListByFilterQueryParams = z.infer<ProductsListByFilterQueryParamsZod>;

  export type ProductsListByFilterQueryParamsPaginatedResult = PaginatedResult<
    'productsListByFilterQueryParams',
    ProductsListByFilterQueryParams
  >;

  export type VendorsListByFilterResponseZod = ReturnType<typeof vendorsListByFilterResponseSchema>;
  export type VendorsListByFilterResponse = z.infer<VendorsListByFilterResponseZod>;

  export type VendorsListByFilterResponsePaginatedResult = PaginatedResult<
    'vendorsListByFilterResponses',
    VendorsListByFilterResponse
  >;

  export type VendorsListByFilterQueryParamsZod = ReturnType<typeof vendorsListByFilterQueryParamsSchema>;
  export type VendorsListByFilterQueryParams = z.infer<VendorsListByFilterQueryParamsZod>;

  export type VendorsListByFilterQueryParamsPaginatedResult = PaginatedResult<
    'vendorsListByFilterQueryParams',
    VendorsListByFilterQueryParams
  >;

  export type SlidesZod = ReturnType<typeof slidesSchema>;
  export type Slides = z.infer<SlidesZod>;

  export type SlidesPaginatedResult = PaginatedResult<'slides', Slides>;

  export type SectionZod = ReturnType<typeof sectionSchema>;
  export type Section = z.infer<SectionZod>;

  export type SectionPaginatedResult = PaginatedResult<'sections', Section>;

  export type LandingPageResponseZod = ReturnType<typeof landingPageResponseSchema>;
  export type LandingPageResponse = z.infer<LandingPageResponseZod>;

  export type LandingPageResponsePaginatedResult = PaginatedResult<'landingPageResponses', LandingPageResponse>;

  export type AddAddressBodyZod = ReturnType<typeof addAddressBodySchema>;
  export type AddAddressBody = z.infer<AddAddressBodyZod>;

  export type AddAddressBodyPaginatedResult = PaginatedResult<'addAddressBodies', AddAddressBody>;

  export type SendChatMessagePathParamsZod = ReturnType<typeof sendChatMessagePathParamsSchema>;
  export type SendChatMessagePathParams = z.infer<SendChatMessagePathParamsZod>;

  export type SendChatMessagePathParamsPaginatedResult = PaginatedResult<
    'sendChatMessagePathParams',
    SendChatMessagePathParams
  >;

  export type SendChatMessageBodyZod = ReturnType<typeof sendChatMessageBodySchema>;
  export type SendChatMessageBody = z.infer<SendChatMessageBodyZod>;

  export type SendChatMessageBodyPaginatedResult = PaginatedResult<'sendChatMessageBodies', SendChatMessageBody>;

  export type MarkChatMessageAsSeenPathParamsZod = ReturnType<typeof markChatMessageAsSeenPathParamsSchema>;
  export type MarkChatMessageAsSeenPathParams = z.infer<MarkChatMessageAsSeenPathParamsZod>;

  export type MarkChatMessageAsSeenPathParamsPaginatedResult = PaginatedResult<
    'markChatMessageAsSeenPathParams',
    MarkChatMessageAsSeenPathParams
  >;

  export type UpdateChatMessagePathParamsZod = ReturnType<typeof updateChatMessagePathParamsSchema>;
  export type UpdateChatMessagePathParams = z.infer<UpdateChatMessagePathParamsZod>;

  export type UpdateChatMessagePathParamsPaginatedResult = PaginatedResult<
    'updateChatMessagePathParams',
    UpdateChatMessagePathParams
  >;

  export type UpdateChatMessageBodyZod = ReturnType<typeof updateChatMessageBodySchema>;
  export type UpdateChatMessageBody = z.infer<UpdateChatMessageBodyZod>;

  export type UpdateChatMessageBodyPaginatedResult = PaginatedResult<'updateChatMessageBodies', UpdateChatMessageBody>;

  export type UpdateAddressPathParamsZod = ReturnType<typeof updateAddressPathParamsSchema>;
  export type UpdateAddressPathParams = z.infer<UpdateAddressPathParamsZod>;

  export type UpdateAddressPathParamsPaginatedResult = PaginatedResult<
    'updateAddressPathParams',
    UpdateAddressPathParams
  >;

  export type UpdateAddressBodyZod = ReturnType<typeof updateAddressBodySchema>;
  export type UpdateAddressBody = z.infer<UpdateAddressBodyZod>;

  export type UpdateAddressBodyPaginatedResult = PaginatedResult<'updateAddressBodies', UpdateAddressBody>;

  export type DeleteAddressPathParamsZod = ReturnType<typeof deleteAddressPathParamsSchema>;
  export type DeleteAddressPathParams = z.infer<DeleteAddressPathParamsZod>;

  export type DeleteAddressPathParamsPaginatedResult = PaginatedResult<
    'deleteAddressPathParams',
    DeleteAddressPathParams
  >;

  export type GetPublicOrderPathParamsZod = ReturnType<typeof getPublicOrderPathParamsSchema>;
  export type GetPublicOrderPathParams = z.infer<GetPublicOrderPathParamsZod>;

  export type GetPublicOrderPathParamsPaginatedResult = PaginatedResult<
    'getPublicOrderPathParams',
    GetPublicOrderPathParams
  >;

  export type PlaceOrderProductZod = ReturnType<typeof placeOrderProductSchema>;
  export type PlaceOrderProduct = z.infer<PlaceOrderProductZod>;

  export type PlaceOrderProductPaginatedResult = PaginatedResult<'placeOrderProducts', PlaceOrderProduct>;

  export type PlaceOrderBodyZod = ReturnType<typeof placeOrderBodySchema>;
  export type PlaceOrderBody = z.infer<PlaceOrderBodyZod>;

  export type PlaceOrderBodyPaginatedResult = PaginatedResult<'placeOrderBodies', PlaceOrderBody>;

  export type BuyNowBodyZod = ReturnType<typeof buyNowBodySchema>;
  export type BuyNowBody = z.infer<BuyNowBodyZod>;

  export type BuyNowBodyPaginatedResult = PaginatedResult<'buyNowBodies', BuyNowBody>;

  export type GetUserWalletQueryParamsZod = ReturnType<typeof getUserWalletQueryParamsSchema>;
  export type GetUserWalletQueryParams = z.infer<GetUserWalletQueryParamsZod>;

  export type GetUserWalletQueryParamsPaginatedResult = PaginatedResult<
    'getUserWalletQueryParams',
    GetUserWalletQueryParams
  >;

  export type WalletReleasePathParamsZod = ReturnType<typeof walletReleasePathParamsSchema>;
  export type WalletReleasePathParams = z.infer<WalletReleasePathParamsZod>;

  export type WalletReleasePathParamsPaginatedResult = PaginatedResult<
    'walletReleasePathParams',
    WalletReleasePathParams
  >;

  export type WalletReleaseResponseZod = ReturnType<typeof walletReleaseResponseSchema>;
  export type WalletReleaseResponse = z.infer<WalletReleaseResponseZod>;

  export type WalletReleaseResponsePaginatedResult = PaginatedResult<'walletReleaseResponses', WalletReleaseResponse>;

  export type WalletMultiReleasePathParamsZod = ReturnType<typeof walletMultiReleasePathParamsSchema>;
  export type WalletMultiReleasePathParams = z.infer<WalletMultiReleasePathParamsZod>;

  export type WalletMultiReleasePathParamsPaginatedResult = PaginatedResult<
    'walletMultiReleasePathParams',
    WalletMultiReleasePathParams
  >;

  export type WalletMultiReleaseResponseZod = ReturnType<typeof walletMultiReleaseResponseSchema>;
  export type WalletMultiReleaseResponse = z.infer<WalletMultiReleaseResponseZod>;

  export type WalletMultiReleaseResponsePaginatedResult = PaginatedResult<
    'walletMultiReleaseResponses',
    WalletMultiReleaseResponse
  >;

  export type WalletMultiReleaseBodyZod = ReturnType<typeof walletMultiReleaseBodySchema>;
  export type WalletMultiReleaseBody = z.infer<WalletMultiReleaseBodyZod>;

  export type WalletMultiReleaseBodyPaginatedResult = PaginatedResult<
    'walletMultiReleaseBodies',
    WalletMultiReleaseBody
  >;

  export type WalletCapturePathParamsZod = ReturnType<typeof walletCapturePathParamsSchema>;
  export type WalletCapturePathParams = z.infer<WalletCapturePathParamsZod>;

  export type WalletCapturePathParamsPaginatedResult = PaginatedResult<
    'walletCapturePathParams',
    WalletCapturePathParams
  >;

  export type WalletCaptureResponseZod = ReturnType<typeof walletCaptureResponseSchema>;
  export type WalletCaptureResponse = z.infer<WalletCaptureResponseZod>;

  export type WalletCaptureResponsePaginatedResult = PaginatedResult<'walletCaptureResponses', WalletCaptureResponse>;

  export type WalletMultiCapturePathParamsZod = ReturnType<typeof walletMultiCapturePathParamsSchema>;
  export type WalletMultiCapturePathParams = z.infer<WalletMultiCapturePathParamsZod>;

  export type WalletMultiCapturePathParamsPaginatedResult = PaginatedResult<
    'walletMultiCapturePathParams',
    WalletMultiCapturePathParams
  >;

  export type WalletMultiCaptureResponseZod = ReturnType<typeof walletMultiCaptureResponseSchema>;
  export type WalletMultiCaptureResponse = z.infer<WalletMultiCaptureResponseZod>;

  export type WalletMultiCaptureResponsePaginatedResult = PaginatedResult<
    'walletMultiCaptureResponses',
    WalletMultiCaptureResponse
  >;

  export type WalletMultiCaptureBodyZod = ReturnType<typeof walletMultiCaptureBodySchema>;
  export type WalletMultiCaptureBody = z.infer<WalletMultiCaptureBodyZod>;

  export type WalletMultiCaptureBodyPaginatedResult = PaginatedResult<
    'walletMultiCaptureBodies',
    WalletMultiCaptureBody
  >;

  export type WalletRefundPathParamsZod = ReturnType<typeof walletRefundPathParamsSchema>;
  export type WalletRefundPathParams = z.infer<WalletRefundPathParamsZod>;

  export type WalletRefundPathParamsPaginatedResult = PaginatedResult<'walletRefundPathParams', WalletRefundPathParams>;

  export type WalletRefundResponseZod = ReturnType<typeof walletRefundResponseSchema>;
  export type WalletRefundResponse = z.infer<WalletRefundResponseZod>;

  export type WalletRefundResponsePaginatedResult = PaginatedResult<'walletRefundResponses', WalletRefundResponse>;

  export type WalletHoldResponseZod = ReturnType<typeof walletHoldResponseSchema>;
  export type WalletHoldResponse = z.infer<WalletHoldResponseZod>;

  export type WalletHoldResponsePaginatedResult = PaginatedResult<'walletHoldResponses', WalletHoldResponse>;

  export type WalletHoldPathParamsZod = ReturnType<typeof walletHoldPathParamsSchema>;
  export type WalletHoldPathParams = z.infer<WalletHoldPathParamsZod>;

  export type WalletHoldPathParamsPaginatedResult = PaginatedResult<'walletHoldPathParams', WalletHoldPathParams>;

  export type WalletHoldBodyZod = ReturnType<typeof walletHoldBodySchema>;
  export type WalletHoldBody = z.infer<WalletHoldBodyZod>;

  export type WalletHoldBodyPaginatedResult = PaginatedResult<'walletHoldBodies', WalletHoldBody>;

  export type WalletMultiHoldResponseZod = ReturnType<typeof walletMultiHoldResponseSchema>;
  export type WalletMultiHoldResponse = z.infer<WalletMultiHoldResponseZod>;

  export type WalletMultiHoldResponsePaginatedResult = PaginatedResult<
    'walletMultiHoldResponses',
    WalletMultiHoldResponse
  >;

  export type WalletMultiHoldPathParamsZod = ReturnType<typeof walletMultiHoldPathParamsSchema>;
  export type WalletMultiHoldPathParams = z.infer<WalletMultiHoldPathParamsZod>;

  export type WalletMultiHoldPathParamsPaginatedResult = PaginatedResult<
    'walletMultiHoldPathParams',
    WalletMultiHoldPathParams
  >;

  export type WalletMultiHoldBodyZod = ReturnType<typeof walletMultiHoldBodySchema>;
  export type WalletMultiHoldBody = z.infer<WalletMultiHoldBodyZod>;

  export type WalletMultiHoldBodyPaginatedResult = PaginatedResult<'walletMultiHoldBodies', WalletMultiHoldBody>;

  export type WalletTopUpPathParamsZod = ReturnType<typeof walletTopUpPathParamsSchema>;
  export type WalletTopUpPathParams = z.infer<WalletTopUpPathParamsZod>;

  export type WalletTopUpPathParamsPaginatedResult = PaginatedResult<'walletTopUpPathParams', WalletTopUpPathParams>;

  export type WalletTopUpBodyZod = ReturnType<typeof walletTopUpBodySchema>;
  export type WalletTopUpBody = z.infer<WalletTopUpBodyZod>;

  export type WalletTopUpBodyPaginatedResult = PaginatedResult<'walletTopUpBodies', WalletTopUpBody>;

  export type PaymentIntentConfirmMetadataZod = ReturnType<typeof paymentIntentConfirmMetadataSchema>;
  export type PaymentIntentConfirmMetadata = z.infer<PaymentIntentConfirmMetadataZod>;

  export type PaymentIntentConfirmMetadataPaginatedResult = PaginatedResult<
    'paymentIntentConfirmMetadata',
    PaymentIntentConfirmMetadata
  >;

  export type PaymentIntentConfirmBodyZod = ReturnType<typeof paymentIntentConfirmBodySchema>;
  export type PaymentIntentConfirmBody = z.infer<PaymentIntentConfirmBodyZod>;

  export type PaymentIntentConfirmBodyPaginatedResult = PaginatedResult<
    'paymentIntentConfirmBodies',
    PaymentIntentConfirmBody
  >;

  export type CreatePaymentIntentBodyZod = ReturnType<typeof createPaymentIntentBodySchema>;
  export type CreatePaymentIntentBody = z.infer<CreatePaymentIntentBodyZod>;

  export type CreatePaymentIntentBodyPaginatedResult = PaginatedResult<
    'createPaymentIntentBodies',
    CreatePaymentIntentBody
  >;

  export type PaymentIntentResponseZod = ReturnType<typeof paymentIntentResponseSchema>;
  export type PaymentIntentResponse = z.infer<PaymentIntentResponseZod>;

  export type PaymentIntentResponsePaginatedResult = PaginatedResult<'paymentIntentResponses', PaymentIntentResponse>;

  export type WalletTransactionsListResponseZod = ReturnType<typeof walletTransactionsListResponseSchema>;
  export type WalletTransactionsListResponse = z.infer<WalletTransactionsListResponseZod>;

  export type WalletTransactionsListResponsePaginatedResult = PaginatedResult<
    'walletTransactionsListResponses',
    WalletTransactionsListResponse
  >;

  export type WalletTransactionsListQueryParamsZod = ReturnType<typeof walletTransactionsListQueryParamsSchema>;
  export type WalletTransactionsListQueryParams = z.infer<WalletTransactionsListQueryParamsZod>;

  export type WalletTransactionsListQueryParamsPaginatedResult = PaginatedResult<
    'walletTransactionsListQueryParams',
    WalletTransactionsListQueryParams
  >;

  export type ExchangeRateResponseZod = ReturnType<typeof exchangeRateResponseSchema>;
  export type ExchangeRateResponse = z.infer<ExchangeRateResponseZod>;

  export type ExchangeRateResponsePaginatedResult = PaginatedResult<'exchangeRateResponses', ExchangeRateResponse>;

  export type AddFCMTokenBodyZod = ReturnType<typeof addFcmTokenBodySchema>;
  export type AddFCMTokenBody = z.infer<AddFCMTokenBodyZod>;

  export type AddFCMTokenBodyPaginatedResult = PaginatedResult<'addFcmTokenBodies', AddFCMTokenBody>;

  export type RemoveFCMTokenBodyZod = ReturnType<typeof removeFcmTokenBodySchema>;
  export type RemoveFCMTokenBody = z.infer<RemoveFCMTokenBodyZod>;

  export type RemoveFCMTokenBodyPaginatedResult = PaginatedResult<'removeFcmTokenBodies', RemoveFCMTokenBody>;

  export type CategoryCreateBodyZod = ReturnType<typeof categoryCreateBodySchema>;
  export type CategoryCreateBody = z.infer<CategoryCreateBodyZod>;

  export type CategoryCreateBodyPaginatedResult = PaginatedResult<'categoryCreateBodies', CategoryCreateBody>;

  export type VendorCreateBodyZod = ReturnType<typeof vendorCreateBodySchema>;
  export type VendorCreateBody = z.infer<VendorCreateBodyZod>;

  export type VendorCreateBodyPaginatedResult = PaginatedResult<'vendorCreateBodies', VendorCreateBody>;

  export type VendorDeletePathParamsZod = ReturnType<typeof vendorDeletePathParamsSchema>;
  export type VendorDeletePathParams = z.infer<VendorDeletePathParamsZod>;

  export type VendorDeletePathParamsPaginatedResult = PaginatedResult<'vendorDeletePathParams', VendorDeletePathParams>;

  export type VendorUpdatePathParamsZod = ReturnType<typeof vendorUpdatePathParamsSchema>;
  export type VendorUpdatePathParams = z.infer<VendorUpdatePathParamsZod>;

  export type VendorUpdatePathParamsPaginatedResult = PaginatedResult<'vendorUpdatePathParams', VendorUpdatePathParams>;

  export type VendorUpdateBodyZod = ReturnType<typeof vendorUpdateBodySchema>;
  export type VendorUpdateBody = z.infer<VendorUpdateBodyZod>;

  export type VendorUpdateBodyPaginatedResult = PaginatedResult<'vendorUpdateBodies', VendorUpdateBody>;

  export type ProductDeletePathParamsZod = ReturnType<typeof productDeletePathParamsSchema>;
  export type ProductDeletePathParams = z.infer<ProductDeletePathParamsZod>;

  export type ProductDeletePathParamsPaginatedResult = PaginatedResult<
    'productDeletePathParams',
    ProductDeletePathParams
  >;

  export type ProductUpdatePathParamsZod = ReturnType<typeof productUpdatePathParamsSchema>;
  export type ProductUpdatePathParams = z.infer<ProductUpdatePathParamsZod>;

  export type ProductUpdatePathParamsPaginatedResult = PaginatedResult<
    'productUpdatePathParams',
    ProductUpdatePathParams
  >;

  export type ProductUpdateBodyZod = ReturnType<typeof productUpdateBodySchema>;
  export type ProductUpdateBody = z.infer<ProductUpdateBodyZod>;

  export type ProductUpdateBodyPaginatedResult = PaginatedResult<'productUpdateBodies', ProductUpdateBody>;

  export type ProductCreatePathParamsZod = ReturnType<typeof productCreatePathParamsSchema>;
  export type ProductCreatePathParams = z.infer<ProductCreatePathParamsZod>;

  export type ProductCreatePathParamsPaginatedResult = PaginatedResult<
    'productCreatePathParams',
    ProductCreatePathParams
  >;

  export type ProductCreateBodyZod = ReturnType<typeof productCreateBodySchema>;
  export type ProductCreateBody = z.infer<ProductCreateBodyZod>;

  export type ProductCreateBodyPaginatedResult = PaginatedResult<'productCreateBodies', ProductCreateBody>;

  export type ProductReviewCreateBodyZod = ReturnType<typeof productReviewCreateBodySchema>;
  export type ProductReviewCreateBody = z.infer<ProductReviewCreateBodyZod>;

  export type ProductReviewCreateBodyPaginatedResult = PaginatedResult<
    'productReviewCreateBodies',
    ProductReviewCreateBody
  >;
}

/**
 * Represents a paginated result containing a list of hydrated documents and pagination information.
 * @template K - The plural name of the _.
 * @template T - The type of the hydrated documents.
 */
export type PaginatedResult<K extends string, T> = {
  [P in K]: T[];
} & {
  pagination: {
    page: number;
  };
};
